// src/components/pages/CourseCurriculum.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "../Styles/LmsExtra.css";
import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronUp,
  FiEye,
  FiTrash2,
  FiEdit2,
  FiFileText,
  FiCheckCircle,
  FiHelpCircle,
  FiClipboard,
  FiMessageSquare,
  FiClock,
} from "react-icons/fi";

import { API_BASE } from "../../../api";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import { cursosNormalize } from "../Objetos/CursosModel";
import { moduloNormalize } from "../Objetos/ModuloModel";
import { leccionesNormalize } from "../Objetos/LeccionesModel";
import ConfirmDeleteDialog from "../../security/ConfirmDeleteDialog";
import SolicitarCertificadoModal from "../Components/SolicitarCertificadoModal";
import AlertDialog from "../Components/Shared/AlertDialog";
import ConfirmDialog from "../Components/Shared/ConfirmDialog";
import ObservacionesModal from "../Modals/ObservacionesModal";
import { useLogos } from "../../../context/LogoProvider";

const CourseCurriculum = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getSystemConfig } = useLogos();

  const handleObservationReferenceClick = (ref) => {
    if (!ref) return;

    if (ref.tipo === 'LECCION') {
      navigate("/menu/lms/lecciones", {
        state: {
          lessonId: ref.idReferencial,
          hideNavigation: false,
          idCurso: course?.idCurso
        },
      });
    } else if (ref.tipo === 'CUESTIONARIO') {
      navigate(`/menu/lms/cuestionario/${ref.idReferencial}/preguntas`, {
        state: { idCurso: course?.idCurso }
      });
    } else if (ref.tipo === 'EXAMEN') {
      navigate(`/menu/lms/examen/${ref.idReferencial}/preguntas`, {
        state: { idCurso: course?.idCurso }
      });
    } else {
      // Fallback for unknown types or modules?
      console.warn("Unknown reference type for navigation:", ref.tipo);
    }
  };

  const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");
  const COURSE_IMAGE_UPLOAD_ENDPOINT = `${API_BASE}/upload/curso-imagen`;


  // ---------- State principal ----------
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [expandedModules, setExpandedModules] = useState({});
  const [isObservacionesModalOpen, setIsObservacionesModalOpen] = useState(false);

  // Edición de títulos (curso / módulo / lección)
  const [editingCourseTitle, setEditingCourseTitle] = useState(false);
  const [courseTitleDraft, setCourseTitleDraft] = useState("");

  const [editingModuleId, setEditingModuleId] = useState(null);
  const [moduleTitleDraft, setModuleTitleDraft] = useState("");

  const [editingLesson, setEditingLesson] = useState(null); // { moduleId, lessonId }
  const [lessonTitleDraft, setLessonTitleDraft] = useState("");

  // Estados para diálogos de eliminación
  const [deleteModuleDialog, setDeleteModuleDialog] = useState({
    open: false,
    moduleId: null,
    moduleName: "",
  });
  const [deleteLessonDialog, setDeleteLessonDialog] = useState({
    open: false,
    moduleId: null,
    lessonId: null,
    lessonName: "",
  });
  const [deleteQuizDialog, setDeleteQuizDialog] = useState({
    open: false,
    moduleId: null,
    quizId: null,
    quizName: "",
  });
  const [deleteExamDialog, setDeleteExamDialog] = useState({
    open: false,
    moduleId: null,
    examId: null,
    examName: "",
  });

  // Edición de cuestionarios
  const [editingQuiz, setEditingQuiz] = useState(null); // { moduleId, quizId }
  const [quizTitleDraft, setQuizTitleDraft] = useState("");

  // Edición de exámenes
  const [editingExam, setEditingExam] = useState(null); // { moduleId, examId }
  const [examTitleDraft, setExamTitleDraft] = useState("");

  // Drag & Drop para reordenar contenido
  const [draggedItem, setDraggedItem] = useState(null); // { moduleId, item }
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Drag & Drop para reordenar MÓDULOS
  const [draggedModule, setDraggedModule] = useState(null);
  const [dragOverModuleIndex, setDragOverModuleIndex] = useState(null);
  const [isDragHandleActive, setIsDragHandleActive] = useState(false);



  // Modal "Nuevo" (Lección / Cuestionario / Evaluación)
  const [newContentModal, setNewContentModal] = useState({
    open: false,
    moduleId: null,
  });

  // Modal de Configuración del Curso
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configDraft, setConfigDraft] = useState({
    nombre: "",
    duracion: "",
    idNivel: "",
    idTipoHabilidad: "",
    urlRutaImagen: "",
    monedasPorLeccion: "",
    autoAsignable: false,
  });
  const [configMonedasBaseline, setConfigMonedasBaseline] = useState(0);
  const [configImagePreview, setConfigImagePreview] = useState(null);
  const [configImageUploading, setConfigImageUploading] = useState(false);
  const [configImageError, setConfigImageError] = useState("");
  const [niveles, setNiveles] = useState([]);
  const [tiposHabilidad, setTiposHabilidad] = useState([]);
  const [hasCertificate, setHasCertificate] = useState(false);

  // Modal Solicitar Certificado
  const [solicitarCertModalOpen, setSolicitarCertModalOpen] = useState(false);

  // Estados para modales de alerta y confirmación
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });




  // ---------- Función para cargar curso completo desde BD ----------
  const loadCursoCompleto = async (showLoading = true) => {
    if (!slug) {
      setLoading(false);
      return;
    }

    if (showLoading) setLoading(true);
    try {
      // 1. Obtener Curso (Optimizado)
      let cursoEncontrado = null;
      try {
        const respCurso = await axios.get(LMS_ENDPOINTS.cursosById(slug));
        const cursoData = Array.isArray(respCurso.data) ? respCurso.data[0] : respCurso.data;
        if (cursoData) {
          cursoEncontrado = cursosNormalize(cursoData);
        }
      } catch (e) {
        console.warn("Error cargando curso por ID:", e);
      }

      if (!cursoEncontrado) {
        console.warn("Curso no encontrado en la lista de BD");
        setLoading(false);
        return;
      }

      const idCurso = cursoEncontrado.idCurso;

      // 2. Cargar SOLO los componentes del curso actual en paralelo
      const [respModulos, respModuloContenido, respLecciones, respCuestionarios, respExamenes] = await Promise.all([
        axios.get(LMS_ENDPOINTS.moduloByCurso(idCurso)),
        axios.get(LMS_ENDPOINTS.moduloContenidoByCurso(idCurso)),
        axios.get(LMS_ENDPOINTS.leccionesByCurso(idCurso)),
        axios.get(LMS_ENDPOINTS.cuestionarioByCurso(idCurso)),
        axios.get(LMS_ENDPOINTS.examenByCurso(idCurso))
      ]);

      const modulosDelCurso = (respModulos.data || [])
        .map(moduloNormalize)
        .sort((a, b) => (a.orden || 0) - (b.orden || 0));

      const todoModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);
      const todasLecciones = (respLecciones.data || []).map(leccionesNormalize);
      const todosCuestionarios = respCuestionarios.data || [];
      const todosExamenes = respExamenes.data || [];


      // 4. Mapear módulos con su contenido ORDENADO según ModuloContenido
      const modulosMapeados = modulosDelCurso.map((mod) => {
        // Obtener ModuloContenido de este módulo
        const contenidosDelModulo = todoModuloContenido
          .filter((mc) => mc.idModulo === mod.idModulo && mc.activo)
          .sort((a, b) => (a.orden || 0) - (b.orden || 0)); // Ordenar por campo orden

        // Mapear cada contenido a su tipo específico
        const contenidoMapeado = contenidosDelModulo.map((mc) => {
          const tipo = mc.tablaContenido;

          if (tipo === "Lecciones") {
            const leccion = todasLecciones.find(l => l.idLeccion === mc.idAsociado);
            if (!leccion) {
              console.warn(`?? Lección ${mc.idAsociado} no encontrada`);
              return null;
            }

            // Validar que la lección esté activa
            if (!(leccion.activo ?? leccion.Activo ?? true)) return null;

            return {
              tipo: "LECCION",
              id: leccion.idLeccion,
              idLeccion: leccion.idLeccion,
              idModulo: leccion.idModulo,
              titulo: leccion.titulo,
              monedas: leccion.monedas || 0,
              activo: leccion.activo,
              completado: false, // Requiere LeccionEstudiante
              idModuloContenido: mc.idModuloContenido
            };
          }

          if (tipo === "Cuestionario") {
            const cuestionario = todosCuestionarios.find(
              q => (q.idCuestionario || q.IdCuestionario) === mc.idAsociado
            );
            if (!cuestionario) {
              console.warn(`?? Cuestionario ${mc.idAsociado} no encontrado`);
              return null;
            }

            // Validar que esté activo
            if (!(cuestionario.activo ?? cuestionario.Activo ?? true)) return null;

            return {
              tipo: "CUESTIONARIO",
              id: cuestionario.idCuestionario || cuestionario.IdCuestionario,
              idCuestionario: cuestionario.idCuestionario || cuestionario.IdCuestionario,
              titulo: cuestionario.nombre || cuestionario.Nombre,
              activo: cuestionario.activo !== undefined ? cuestionario.activo : cuestionario.Activo,
              idModuloContenido: mc.idModuloContenido
            };
          }

          if (tipo === "Examen") {
            const examen = todosExamenes.find(
              e => (e.idExamen || e.IdExamen) === mc.idAsociado
            );
            if (!examen) {
              console.warn(`?? Examen ${mc.idAsociado} no encontrado`);
              return null;
            }

            // Validar que esté activo
            if (!(examen.activo ?? examen.Activo ?? true)) return null;

            return {
              tipo: "EXAMEN",
              id: examen.idExamen || examen.IdExamen,
              idExamen: examen.idExamen || examen.IdExamen,
              titulo: examen.nombre || examen.Nombre,
              activo: examen.activo !== undefined ? examen.activo : examen.Activo,
              idModuloContenido: mc.idModuloContenido
            };
          }

          console.warn(`?? Tipo de contenido no soportado: ${tipo}`);
          return null;
        }).filter(Boolean); // Eliminar nulls

        return {
          id: mod.idModulo,
          idModulo: mod.idModulo,
          idCurso: mod.idCurso,
          titulo: mod.nombre,
          nombre: mod.nombre,
          orden: mod.orden,
          activo: mod.activo,
          contenido: contenidoMapeado, // Array unificado de lecciones, cuestionarios, etc.
          // Mantener compatibilidad con código existente
          lecciones: contenidoMapeado.filter(c => c.tipo === "LECCION")
        };
      });

      // 5. Contar cuestionarios totales del curso
      const cuestionariosDelCurso = todosCuestionarios.filter(
        (q) => (q.idCurso || q.IdCurso) === cursoEncontrado.idCurso
      );

      const cuestionariosMapeados = cuestionariosDelCurso.map((q) => ({
        idCuestionario: q.idCuestionario || q.IdCuestionario,
        idCurso: q.idCurso || q.IdCurso,
        nombre: q.nombre || q.Nombre,
        activo:
          q.activo !== undefined
            ? q.activo
            : q.Activo !== undefined
              ? q.Activo
              : true,
      }));

      // 6. Calcular duracion real basada en estructura
      const totalActividades = modulosMapeados.reduce((acc, m) => acc + (m.contenido || []).length, 0);

      setCourse({
        ...cursoEncontrado,
        titulo: cursoEncontrado.nombre,
        duracion: totalActividades, // USAR SIEMPRE EL CALCULADO
        duracionSemanas: Math.ceil(totalActividades / 40) || 1,
        estudiantes: 0, // Placeholder
        cuestionarios: cuestionariosMapeados.length,
        cuestionariosDetalle: cuestionariosMapeados,
        modulos: modulosMapeados,
      });

      // 6. SI LA DURACION EN BD ES DIFERENTE, ACTUALIZARLA EN SEGUNDO PLANO
      if (totalActividades !== cursoEncontrado.duracion) {
        console.log(`[AutoDuration] Syncing DB duration: ${cursoEncontrado.duracion} -> ${totalActividades}`);
        const cursoSyncDTO = {
            idCurso: cursoEncontrado.idCurso,
            idInstructor: cursoEncontrado.idInstructor || 0,
            nombre: cursoEncontrado.nombre || "",
            idNivel: cursoEncontrado.idNivel || 0,
            idTipoHabilidad: cursoEncontrado.idTipoHabilidad || 0,
            duracion: totalActividades,
            idContenido: cursoEncontrado.idContenido || 0,
            urlRutaImagen: cursoEncontrado.uRLRutaImagen || "",
            autoAsignable: cursoEncontrado.autoAsignable ?? false,
            terminado: cursoEncontrado.terminado ?? false,
            activo: cursoEncontrado.activo !== undefined ? cursoEncontrado.activo : true,
        };
        axios.put(LMS_ENDPOINTS.cursos, cursoSyncDTO).catch(err => console.error("Error syncing duration:", err));
      }

      setError(null);
    } catch (err) {
      console.error("Error al cargar curso completo desde BD:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // ---------- useEffect: Cargar curso al montar ----------
  useEffect(() => {
    loadCursoCompleto();
  }, [slug]);

  useEffect(() => {
    if (course) {
      console.log(`[Course Info] Loaded course. Terminado: ${course.terminado}`);
    }
  }, [course]);

  // Mantener expandedModules sincronizado con los módulos actuales
  useEffect(() => {
    if (!course) return;
    setExpandedModules((prev) => {
      const next = { ...prev };
      (course.modulos || []).forEach((m) => {
        if (!(m.id in next)) {
          next[m.id] = true;
        }
      });
      return next;
    });
  }, [course]);

  // ---------- Cálculo de stats ----------
  const stats = useMemo(() => {
    if (!course) {
      return {
        totalModules: 0,
        totalLessons: 0,
        totalQuizzes: 0,
        completedLessons: 0,
        durationWeeks: 0,
        estudiantes: 0,
        cuestionarios: 0,
      };
    }
    let totalModules = (course.modulos || []).length;
    let totalLessons = 0;
    let totalQuizzes = 0;
    let totalExams = 0;
    let completedLessons = 0;

    (course.modulos || []).forEach((m) => {
      (m.contenido || []).forEach((item) => {
        if (item.tipo === "LECCION") {
          totalLessons += 1;
          if (item.completado) completedLessons += 1;
        } else if (item.tipo === "CUESTIONARIO") {
          totalQuizzes += 1;
        } else if (item.tipo === "EXAMEN") {
          totalExams += 1;
        }
      });
    });

    return {
      totalModules,
      totalLessons,
      totalQuizzes,
      totalExams,
      completedLessons,
      durationWeeks: course.duracionSemanas || 0,
      estudiantes: course.estudiantes || 0,
      cuestionarios:
        (course.cuestionariosDetalle || []).length || 0,
    };
  }, [course]);

  const allExpanded = useMemo(() => {
    if (!course || !course.modulos || course.modulos.length === 0)
      return true;
    return course.modulos.every((m) => expandedModules[m.id]);
  }, [course, expandedModules]);

  // Verificar si el curso tiene certificación configurada
  useEffect(() => {
    const checkCertificate = async () => {
      if (!course || !course.idCurso) {
        setHasCertificate(false);
        return;
      }

      try {
        const response = await axios.get(LMS_ENDPOINTS.certificaciones);
        const certificaciones = response.data || [];

        const certificacionDelCurso = certificaciones.find(
          (cert) => {
            const idCursoCert = cert.idCurso || cert.IdCurso;
            return Number(idCursoCert) === Number(course.idCurso);
          }
        );

        setHasCertificate(!!certificacionDelCurso);
      } catch (error) {
        console.error('Error al verificar certificación:', error);
        setHasCertificate(false);
      }
    };

    checkCertificate();
  }, [course]);


  // ---------- Persistencia helper ----------
  const persistCourse = async (updatedCourse) => {
    if (!updatedCourse || !updatedCourse.idCurso) {
      console.error("No se puede guardar: curso sin ID");
      return;
    }

    setCourse(updatedCourse);

    try {
      const cursoDTO = {
        idCurso: updatedCourse.idCurso,
        idInstructor: updatedCourse.idInstructor || 0,
        nombre: updatedCourse.titulo || updatedCourse.nombre || "",
        idNivel: updatedCourse.idNivel || 0,
        idTipoHabilidad: updatedCourse.idTipoHabilidad || 0,
        duracion: updatedCourse.duracion || 0,
        idContenido: updatedCourse.idContenido || 0,
        urlRutaImagen: updatedCourse.uRLRutaImagen || "",
        autoAsignable: updatedCourse.autoAsignable ?? false,
        activo:
          updatedCourse.activo !== undefined
            ? updatedCourse.activo
            : true,
      };

      await axios.put(LMS_ENDPOINTS.cursos, cursoDTO);
      console.log("? Curso actualizado exitosamente en BD");
    } catch (error) {
      console.error("? Error al guardar cambios del curso:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudieron guardar los cambios en la base de datos.",
        variant: "error"
      });
    }
  };

  // ---------- Persistencia helper para módulos ----------
  const persistModule = async (updatedModule) => {
    if (!updatedModule || !updatedModule.idModulo) {
      console.error("No se puede guardar: módulo sin ID");
      return false;
    }

    try {
      const moduloDTO = {
        IdModulo: updatedModule.idModulo,
        IdCurso: updatedModule.idCurso,
        Nombre: updatedModule.titulo || updatedModule.nombre || "",
        Orden: updatedModule.orden || 0,
        Activo:
          updatedModule.activo !== undefined
            ? updatedModule.activo
            : true,
      };

      const response = await axios.put(LMS_ENDPOINTS.modulo, moduloDTO);

      if (response.data) {
        console.log("? Módulo actualizado exitosamente en BD");
        return true;
      }
      return false;
    } catch (error) {
      console.error("? Error al guardar cambios del módulo:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudieron guardar los cambios del módulo en la base de datos.",
        variant: "error"
      });
      return false;
    }
  };

  // ---------- Persistencia helper para lecciones ----------
  const persistLesson = async (updatedLesson) => {
    if (!updatedLesson || !updatedLesson.idLeccion) {
      console.error("No se puede guardar: lección sin ID");
      return false;
    }

    try {
      const leccionDTO = {
        IdLeccion: updatedLesson.idLeccion,
        IdModulo: updatedLesson.idModulo,
        Titulo: updatedLesson.titulo || "",
        Monedas: updatedLesson.monedas || 0,
        Activo:
          updatedLesson.activo !== undefined
            ? updatedLesson.activo
            : true,
      };

      const response = await axios.put(
        LMS_ENDPOINTS.lecciones,
        leccionDTO
      );

      if (response.data) {
        console.log("? Lección actualizada exitosamente en BD");
        return true;
      }
      return false;
    } catch (error) {
      console.error("? Error al guardar cambios de la lección:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudieron guardar los cambios de la lección en la base de datos.",
        variant: "error"
      });
      return false;
    }
  };

  // ---------- Edición: título de cuestionario ----------
  const startEditQuizTitle = (moduleId, quizId, currentTitle) => {
    setEditingLesson(null);
    setEditingExam(null);
    setEditingQuiz({ moduleId, quizId });
    setQuizTitleDraft(currentTitle);
  };

  const saveQuizTitle = async () => {
    if (!editingQuiz || !course) return;

    const trimmed = quizTitleDraft.trim();
    if (!trimmed) {
      setAlertDialog({
        open: true,
        title: "Campo requerido",
        message: "El título del cuestionario no puede estar vacío.",
        variant: "warning"
      });
      return;
    }

    const modulo = course.modulos.find(
      (m) => m.id === editingQuiz.moduleId
    );
    const quiz = modulo?.contenido?.find(
      (c) => c.tipo === "CUESTIONARIO" && c.id === editingQuiz.quizId
    );

    if (!quiz) {
      setEditingQuiz(null);
      return;
    }

    if (quiz.titulo === trimmed) {
      setEditingQuiz(null);
      return;
    }

    // Actualizar estado local primero para ver el cambio inmediatamente
    const updated = {
      ...course,
      modulos: course.modulos.map((m) => {
        if (m.id !== editingQuiz.moduleId) return m;
        return {
          ...m,
          contenido: m.contenido.map((c) =>
            c.tipo === "CUESTIONARIO" && c.id === editingQuiz.quizId
              ? { ...c, titulo: trimmed }
              : c
          ),
        };
      }),
    };
    setCourse(updated);
    setEditingQuiz(null);

    // Luego guardar en BD
    try {
      const quizDTO = {
        idCuestionario: quiz.idCuestionario,
        idCurso: course.idCurso,
        nombre: trimmed,
        activo: quiz.activo !== undefined ? quiz.activo : true,
      };

      await axios.put(
        LMS_ENDPOINTS.cuestionario,
        quizDTO
      );

      console.log("? Título del cuestionario actualizado en BD");
    } catch (error) {
      console.error("? Error al actualizar título del cuestionario:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudieron guardar los cambios del cuestionario en la base de datos.",
        variant: "error"
      });
      // Recargar en caso de error
      await loadCursoCompleto();
    }
  };

  const handleQuizTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveQuizTitle();
    } else if (e.key === "Escape") {
      setEditingQuiz(null);
    }
  };

  useEffect(() => {
    const loadCatalogos = async () => {
      try {
        const [respNiveles, respTiposHabilidad] = await Promise.all([
          axios.get(LMS_ENDPOINTS.catNivel),
          axios.get(LMS_ENDPOINTS.catTipoHabilidad)
        ]);
        setNiveles((respNiveles.data || []).map(Models.niveles));
        setTiposHabilidad((respTiposHabilidad.data || []).map(Models.tipoHabilidad));
      } catch (error) {
        console.error('Error al cargar catalogos:', error);
      }
    };
    loadCatalogos();
  }, []);

  // ---------- Validación de módulos vacíos ----------
  const validateModulesNotEmpty = () => {
    if (!course || !course.modulos || course.modulos.length === 0) return true;

    const emptyModules = course.modulos.filter(m => (m.contenido || []).length === 0);

    if (emptyModules.length > 0) {
      const moduleList = emptyModules.map(m => (
        <li key={m.id}>{m.titulo}</li>
      ));

      setAlertDialog({
        open: true,
        title: "Módulos vacíos",
        message: (
          <div>
            <p>No se puede continuar. Los siguientes módulos no tienen contenido (lecciones, cuestionarios o evaluaciones):</p>
            <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
              {moduleList}
            </ul>
            <p>Por favor agrega contenido a todos los módulos o elimina los módulos vacíos.</p>
          </div>
        ),
        variant: "error"
      });
      return false;
    }

    return true;
  };

  // ---------- Validación de contenido de lecciones ----------
  const validateLessonsContent = async () => {
    try {
      if (!course) return false;

      // 1. Obtener todas las lecciones del curso actual
      const courseLessons = [];
      course.modulos.forEach(m => {
        m.contenido.forEach(c => {
          if (c.tipo === "LECCION") {
            courseLessons.push({
              id: c.idLeccion,
              titulo: c.titulo,
              modulo: m.nombre
            });
          }
        });
      });

      if (courseLessons.length === 0) {
        setAlertDialog({
          open: true,
          title: "Curso vacío",
          message: "El curso no tiene lecciones. Agrega lecciones antes de continuar.",
          variant: "warning"
        });
        return false;
      }

      // 2. Verificar contenido de lecciones en paralelo (Optimizado)
      const emptyLessons = [];

      await Promise.all(courseLessons.map(async (lesson) => {
        try {
          const res = await axios.get(LMS_ENDPOINTS.leccionesContenidoByLeccion(lesson.id));
          const contenidos = res.data || [];

          const hasActiveContent = contenidos.some(c =>
            (c.activo !== undefined ? c.activo : c.Activo) === true
          );

          if (!hasActiveContent) {
            emptyLessons.push(lesson);
          }
        } catch (error) {
          console.warn(`No se pudo verificar contenido para lección ${lesson.id}`, error);
          emptyLessons.push(lesson);
        }
      }));

      if (emptyLessons.length > 0) {
        const lessonList = emptyLessons.map(l => (
          <li key={l.id}>{l.titulo} (Módulo: {l.modulo})</li>
        ));

        setAlertDialog({
          open: true,
          title: "Lecciones sin contenido",
          message: (
            <div>
              <p>No se puede continuar. Las siguientes lecciones no tienen contenido:</p>
              <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                {lessonList}
              </ul>
              <p>Por favor agrega contenido a todas las lecciones.</p>
            </div>
          ),
          variant: "error"
        });
        return false;
      }

      return true;

    } catch (error) {
      console.error("Error al validar contenido de lecciones:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "Ocurrió un error al validar el contenido del curso.",
        variant: "error"
      });
      return false;
    }
  };

  // ---------- Validación de evaluación (examen) ----------
  const validateExamsContent = async () => {
    try {
      if (!course) return false;

      // 1. Identificar exámenes del curso
      const courseExams = [];
      course.modulos.forEach(m => {
        (m.contenido || []).forEach(c => {
          if (c.tipo === "EXAMEN") {
            courseExams.push({
              id: c.idExamen || c.id,
              titulo: c.titulo,
              modulo: m.nombre || m.titulo
            });
          }
        });
      });

      // 2. Si no hay exámenes, error según requerimiento
      if (courseExams.length === 0) {
        setAlertDialog({
          open: true,
          title: "Falta Evaluación",
          message: "El curso debe tener al menos una evaluación (examen) configurada antes de solicitar el certificado o subir el curso.",
          variant: "warning"
        });
        return false;
      }

      // 3. Verificar preguntas de cada examen (Optimizado)
      const emptyExams = [];

      await Promise.all(courseExams.map(async (exam) => {
        try {
          const res = await axios.get(LMS_ENDPOINTS.examenPreguntasByExamen(exam.id));
          const preguntas = res.data || [];
          if (preguntas.length === 0) {
            emptyExams.push(exam);
          }
        } catch (error) {
          // Si da error (ej. 404), asumimos que no tiene preguntas
          console.warn(`No se pudieron obtener preguntas para examen ${exam.id}`, error);
          emptyExams.push(exam);
        }
      }));

      if (emptyExams.length > 0) {
        const examList = emptyExams.map(e => (
          <li key={e.id}>{e.titulo} (Módulo: {e.modulo})</li>
        ));

        setAlertDialog({
          open: true,
          title: "Evaluaciones sin preguntas",
          message: (
            <div>
              <p>No se puede continuar. Las siguientes evaluaciones no tienen preguntas configuradas:</p>
              <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                {examList}
              </ul>
              <p>Por favor agrega al menos una pregunta a cada evaluación.</p>
            </div>
          ),
          variant: "error"
        });
        return false;
      }

      return true;

    } catch (error) {
      console.error("Error al validar contenido de exámenes:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "Ocurrió un error al validar las evaluaciones del curso.",
        variant: "error"
      });
      return false;
    }
  };

  // ---------- Función para solicitar certificado o subir curso ----------
  const handleRequestCertificate = async () => {
    // Validar que no haya módulos vacíos
    if (!validateModulesNotEmpty()) return;

    // Validar que todas las lecciones tengan contenido
    const isLessonsValid = await validateLessonsContent();
    if (!isLessonsValid) return;

    // Validar que exista al menos un examen y tenga preguntas
    const isExamsValid = await validateExamsContent();
    if (!isExamsValid) return;

    if (hasCertificate) {
      // Si ya tiene certificado, publicar el curso
      handlePublishCourse();
    } else {
      // Si no tiene certificado, abrir modal para solicitarlo
      setSolicitarCertModalOpen(true);
    }
  };

  // ---------- Función para publicar curso ----------
  const handlePublishCourse = async () => {
    if (!course || !course.idCurso) return;

    // Mostrar confirmación de publicación
    setConfirmDialog({
      open: true,
      title: 'Confirmar publicación',
      message: '¿Estás seguro de que deseas publicar este curso? Una vez publicado, no podrás modificar su contenido.',
      onConfirm: async () => {
        await proceedWithPublish();
      },
      variant: 'warning'
    });
  };

  const proceedWithPublish = async () => {

    try {
      const cursoDTO = {
        idCurso: course.idCurso,
        idInstructor: course.idInstructor || 0,
        nombre: course.titulo || course.nombre || "",
        idNivel: course.idNivel || 0,
        duracion: course.duracion || 0,
        idContenido: course.idContenido || 0,
        idTipoHabilidad: course.idTipoHabilidad || 0,
        urlRutaImagen: course.uRLRutaImagen || "",
        terminado: true, // Marcar como publicado
        activo: true,
      };

      await axios.put(LMS_ENDPOINTS.cursos, cursoDTO);
      setAlertDialog({
        open: true,
        title: 'Exito!',
        message: 'Curso publicado exitosamente!',
        variant: 'success'
      });

      // Recargar el curso para reflejar los cambios
      await loadCursoCompleto();
    } catch (error) {
      console.error('Error al publicar el curso:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Intente de nuevo más tarde. Gracias',
        variant: 'error'
      });
    }
  };


  // ---------- Funciones del Modal de Configuración ----------
  const handleOpenConfigModal = async () => {
    if (!course) return;

    // Buscar la primera lección del curso para obtener las monedas
    let monedasActuales = "";
    try {
      // Obtener SOLO lecciones de este curso (OPTIMIZADO)
      const leccionesResponse = await axios.get(LMS_ENDPOINTS.leccionesByCurso(course.idCurso));
      const leccionesDelCurso = (leccionesResponse.data || []).map(leccionesNormalize);

      // Usamos la primera que encontremos (si hay alguna)
      if (leccionesDelCurso.length > 0) {
        monedasActuales = String(leccionesDelCurso[0].monedas);
        console.log(`? Monedas obtenidas de la primera lección: ${monedasActuales}`);
      }
    } catch (error) {
      console.error('Error al obtener monedas de lecciones:', error);
    }

    setConfigMonedasBaseline(Number(monedasActuales) || 0);

    // Calcular duración automática: 1 día por cada actividad (lección, examen, cuestionario)
    let totalActividades = 0;
    if (course.modulos) {
      course.modulos.forEach(m => {
        totalActividades += (m.contenido || []).length;
      });
    }

    // Cargar datos actuales del curso en el draft
    setConfigDraft({
      nombre: course.titulo || course.nombre || "",
      duracion: totalActividades > 0 ? totalActividades : (course.duracion || ""),
      idNivel: course.idNivel || "",
      idTipoHabilidad: course.idTipoHabilidad || "",
      urlRutaImagen: course.uRLRutaImagen || "",
      monedasPorLeccion: monedasActuales, // ? Pre-llenar con monedas de la primera lección
      autoAsignable: course.autoAsignable ?? false,
    });

    // Si hay imagen, cargar preview
    if (course.uRLRutaImagen) {
      const imageUrl = course.uRLRutaImagen.startsWith('http')
        ? course.uRLRutaImagen
        : `${SERVER_URL}${course.uRLRutaImagen}`;
      setConfigImagePreview(imageUrl);
    } else {
      setConfigImagePreview(null);
    }

    setConfigImageError("");
    setConfigModalOpen(true);
  };


  const handleConfigImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validaciones
    if (!file.type.startsWith('image/')) {
      setConfigImageError('Por favor selecciona un archivo de imagen válido.');
      return;
    }

    const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
    if (file.size > maxMB * 1024 * 1024) {
      setConfigImageError(`La imagen no debe superar los ${maxMB}MB.`);
      return;
    }

    setConfigImageError('');
    setConfigImageUploading(true);

    // Preview local
    const reader = new FileReader();
    reader.onloadend = () => {
      setConfigImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // 🗑️ BORRAR IMAGEN ANTERIOR DEL S3 SI EXISTE
    const oldImageUrl = course?.uRLRutaImagen || configDraft.urlRutaImagen;
    if (oldImageUrl && oldImageUrl.includes('s3.amazonaws.com')) {
      try {
        console.log('[CourseCurriculum] 🗑️ Eliminando imagen anterior de S3:', oldImageUrl);
        const deleteResponse = await fetch(
          `${COURSE_IMAGE_UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(oldImageUrl)}`,
          { method: 'DELETE' }
        );
        if (deleteResponse.ok) {
          console.log('[CourseCurriculum] ✅ Imagen anterior eliminada de S3');
        }
      } catch (deleteError) {
        console.warn('[CourseCurriculum] ⚠️ Error al eliminar imagen anterior:', deleteError);
      }
    }

    // Subir al servidor
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(COURSE_IMAGE_UPLOAD_ENDPOINT, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al subir la imagen');
      }

      const data = await response.json();
      if (!data.filePath) {
        throw new Error('La respuesta del servidor no incluye filePath.');
      }

      setConfigDraft(prev => ({
        ...prev,
        urlRutaImagen: data.filePath,
      }));
    } catch (error) {
      console.error('[CourseCurriculum] Error al subir imagen:', error);
      setConfigImageError(error.message || 'Error al subir la imagen al servidor.');
      setConfigImagePreview(null);
      setConfigDraft(prev => ({
        ...prev,
        urlRutaImagen: "",
      }));
    } finally {
      setConfigImageUploading(false);
    }
  };

  const handleSaveConfig = async () => {
    const nombre = configDraft.nombre.trim();

    if (!nombre) {
      setAlertDialog({
        open: true,
        title: "Campo requerido",
        message: "Por favor ingresa un nombre para el curso.",
        variant: "warning"
      });
      return;
    }

    if (!configDraft.idNivel) {
      setAlertDialog({
        open: true,
        title: "Campo requerido",
        message: "Por favor selecciona un nivel para el curso.",
        variant: "warning"
      });
      return;
    }

    const monedasDraftValue = configDraft.monedasPorLeccion === ""
      ? null
      : Number(configDraft.monedasPorLeccion);

    // Validar monedas
    if (monedasDraftValue !== null && Number.isFinite(monedasDraftValue)) {
      // Las monedas no pueden ser negativas
      if (monedasDraftValue < 0) {
        setAlertDialog({
          open: true,
          title: "Valor inválido",
          message: "La cantidad de monedas no puede ser negativa.",
          variant: "warning"
        });
        return;
      }

      if (monedasDraftValue > 100) {
        setAlertDialog({
          open: true,
          title: "Valor excedido",
          message: "La cantidad de monedas no puede ser mayor a 100.",
          variant: "warning"
        });
        return;
      }

      // Si el curso ya fue publicado, no se puede disminuir las monedas
      if (course.terminado && monedasDraftValue < configMonedasBaseline) {
        setAlertDialog({
          open: true,
          title: "Restricción de cambio",
          message: `El curso ya fue publicado. La cantidad de monedas por lección no puede disminuir. Actual: ${configMonedasBaseline}.`,
          variant: "warning"
        });
        return;
      }
    }

    try {
      // 1. Actualizar curso
      // Robustez: Enviar ambas versiones (PascalCase y camelCase) para asegurar mapeo en el servidor
      const cursoDTO = {
        IdCurso: Number(course.idCurso || course.IdCurso || 0),
        idCurso: Number(course.idCurso || course.IdCurso || 0),
        IdInstructor: Number(course.idInstructor || course.IdInstructor || 0),
        idInstructor: Number(course.idInstructor || course.IdInstructor || 0),
        Nombre: nombre,
        nombre: nombre,
        IdNivel: Number(configDraft.idNivel),
        idNivel: Number(configDraft.idNivel),
        IdTipoHabilidad: configDraft.idTipoHabilidad ? Number(configDraft.idTipoHabilidad) : 0,
        idTipoHabilidad: configDraft.idTipoHabilidad ? Number(configDraft.idTipoHabilidad) : 0,
        Duracion: Number(configDraft.duracion) || 0,
        duracion: Number(configDraft.duracion) || 0,
        IdContenido: Number(course.idContenido || course.IdContenido || 0),
        idContenido: Number(course.idContenido || course.IdContenido || 0),
        URLRutaImagen: configDraft.urlRutaImagen || "",
        urlRutaImagen: configDraft.urlRutaImagen || "",
        AutoAsignable: Boolean(configDraft.autoAsignable),
        autoAsignable: Boolean(configDraft.autoAsignable),
        Terminado: Boolean(course.terminado ?? course.Terminado ?? false),
        terminado: Boolean(course.terminado ?? course.Terminado ?? false),
        Activo: course.activo !== undefined ? course.activo : (course.Activo !== undefined ? course.Activo : true),
        activo: course.activo !== undefined ? course.activo : (course.Activo !== undefined ? course.Activo : true),
      };

      await axios.put(LMS_ENDPOINTS.cursos, cursoDTO);
      console.log("✅ Datos generales del curso actualizados.");

      // 2. Ejecutar SP de monedas si hay un valor válido configurado
      if (
        monedasDraftValue !== null &&
        Number.isFinite(monedasDraftValue) &&
        monedasDraftValue >= 0
      ) {
        const payloadMonedas = {
          IdCurso: Number(course.idCurso || course.IdCurso || 0),
          idCurso: Number(course.idCurso || course.IdCurso || 0),
          MonedasNueva: Number(monedasDraftValue),
          monedasNueva: Number(monedasDraftValue),
          MonedasNuevas: Number(monedasDraftValue),
          monedasNuevas: Number(monedasDraftValue)
        };

        console.log("🚀 Enviando actualización de monedas:", payloadMonedas);
        await axios.put(LMS_ENDPOINTS.cursosActualizarMonedas, payloadMonedas);
        
        setConfigMonedasBaseline(monedasDraftValue);
        console.log(`✅ Monedas actualizadas exitosamente a: ${monedasDraftValue}`);
      }

      // 3. SIEMPRE validar y asignar monedas a todas las lecciones
      // Esto se ejecuta independientemente de si hubo cambios o no
      if (monedasDraftValue !== null && Number.isFinite(monedasDraftValue) && monedasDraftValue >= 0) {
        console.log("🔍 Validando y asignando monedas a todas las lecciones...");

        // Obtener todas las lecciones del curso
        const respLecciones = await axios.get(LMS_ENDPOINTS.lecciones);
        const todasLecciones = (respLecciones.data || []).map(leccionesNormalize);

        // Obtener módulos del curso
        const respModulos = await axios.get(LMS_ENDPOINTS.modulo);
        const todosModulos = respModulos.data || [];
        const modulosDelCurso = todosModulos.filter(m => m.idModulo === course.idCurso || m.IdCurso === course.idCurso);
        const idsModulosDelCurso = modulosDelCurso.map(m => m.idModulo || m.IdModulo);

        // Filtrar lecciones que pertenecen a este curso
        const leccionesDelCurso = todasLecciones.filter(l =>
          idsModulosDelCurso.includes(l.idModulo)
        );

        // Encontrar lecciones que no tienen la cantidad correcta de monedas
        const leccionesParaActualizar = leccionesDelCurso.filter(l =>
          (l.monedas || 0) !== monedasDraftValue
        );

        if (leccionesParaActualizar.length > 0) {
          console.log(`🔍 Encontradas ${leccionesParaActualizar.length} lecciones con cantidad incorrecta de monedas. Actualizando...`);

          // Actualizar cada lección
          for (const leccion of leccionesParaActualizar) {
            try {
              const leccionDTO = {
                IdLeccion: leccion.idLeccion,
                IdModulo: leccion.idModulo,
                Titulo: leccion.titulo || "",
                Monedas: monedasDraftValue,
                Activo: leccion.activo !== undefined ? leccion.activo : true,
              };

              await axios.put(LMS_ENDPOINTS.lecciones, leccionDTO);
              console.log(`✅ Monedas actualizadas para lección "${leccion.titulo}": ${monedasDraftValue}`);
            } catch (error) {
              console.error(`? Error al actualizar monedas de lección ${leccion.idLeccion}:`, error);
            }
          }
        } else {
          console.log("? Todas las lecciones ya tienen la cantidad correcta de monedas");
        }
      }

      // 4. Recargar curso
      await loadCursoCompleto();

      // 5. Cerrar modal
      setConfigModalOpen(false);
      setConfigDraft({
        nombre: "",
        duracion: "",
        idNivel: "",
        urlRutaImagen: "",
        monedasPorLeccion: "",
      });
      setConfigImagePreview(null);
      setConfigImageError("");

    } catch (error) {
      console.error("? Error Fatal al guardar:", error);
      const serverMsg = error.response?.data?.message || 
                        (typeof error.response?.data === 'string' ? error.response.data : null) || 
                        error.message;
      setAlertDialog({
        open: true,
        title: "Error al guardar",
        message: `El servidor respondió: ${serverMsg}`,
        variant: "error"
      });
    }
  };

  // ---------- Handlers generales ----------
  const handleToggleModule = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  const handleToggleAllModules = () => {
    if (!course) return;
    const nextValue = !allExpanded;
    const next = {};
    (course.modulos || []).forEach((m) => {
      next[m.id] = nextValue;
    });
    setExpandedModules(next);
  };

  const handleToggleLessonCompleted = (moduleId, lessonId) => {
    if (!course) return;
    const updated = {
      ...course,
      modulos: course.modulos.map((m) => {
        if (m.id !== moduleId) return m;
        return {
          ...m,
          lecciones: m.lecciones.map((l) =>
            l.id === lessonId
              ? { ...l, completado: !l.completado }
              : l
          ),
        };
      }),
    };
    persistCourse(updated);
  };

  // ---------- Agregar lección ----------
  const handleAddLesson = (moduleId) => {
    if (!course) return;
    const updated = { ...course };

    const modIndex = updated.modulos.findIndex(
      (m) => m.id === moduleId
    );
    if (modIndex === -1) return;

    const targetModule = updated.modulos[modIndex];
    const currentCount = (targetModule.lecciones || []).length;
    const newLessonId = Date.now();

    const nuevaLeccion = {
      id: newLessonId,
      titulo: `Nueva lección ${currentCount + 1}`,
      duracionMin: 20,
      completado: false,
    };

    updated.modulos[modIndex] = {
      ...targetModule,
      lecciones: [...(targetModule.lecciones || []), nuevaLeccion],
    };

    persistCourse(updated);
  };

  // ---------- Eliminar contenido de S3 ----------
  const deleteLessonContentFromS3 = async (lessonId) => {
    try {
      // 1. Obtener contenidos de la lección
      const response = await axios.get(LMS_ENDPOINTS.leccionesContenido);
      const allContent = response.data || [];
      const lessonContent = allContent.filter(c => c.idLeccion === parseInt(lessonId) && c.activo);

      const elementsToDelete = [];

      // 2. Parsear y buscar elementos de S3
      lessonContent.forEach(item => {
        try {
          const parsedValue = JSON.parse(item.valor);
          // Verificar si es una sección con hijos
          if (parsedValue.children && Array.isArray(parsedValue.children)) {
            parsedValue.children.forEach(column => {
              if (column.children && Array.isArray(column.children)) {
                column.children.forEach(element => {
                  if ((element.type === 'video' || element.type === 'image') &&
                    element.src &&
                    element.src.includes('s3.amazonaws.com')) {
                    elementsToDelete.push(element);
                  }
                });
              }
            });
          }
        } catch (e) {
          console.warn('Error parsing lesson content during deletion check:', e);
        }
      });

      // 3. Eliminar archivos de S3
      if (elementsToDelete.length > 0) {
        console.log(`[Cleaner] ??? Eliminando ${elementsToDelete.length} archivos de S3 para la lección ${lessonId}...`);

        // Endpoints de borrado (construidos dinamicamente)
        const VIDEO_DELETE_ENDPOINT = `${API_BASE}/upload/lesson-video`;
        const IMAGE_DELETE_ENDPOINT = `${API_BASE}/upload/lesson-image`;

        const deletePromises = elementsToDelete.map(async (element) => {
          let deleteEndpoint;
          if (element.type === 'video') {
            deleteEndpoint = VIDEO_DELETE_ENDPOINT;
          } else if (element.type === 'image') {
            deleteEndpoint = IMAGE_DELETE_ENDPOINT;
          }

          if (deleteEndpoint) {
            try {
              const deleteResponse = await fetch(
                `${deleteEndpoint}?filePath=${encodeURIComponent(element.src)}`,
                { method: 'DELETE' }
              );
              if (deleteResponse.ok) {
                console.log(`[Cleaner] ? Archivo eliminado de S3: ${element.src}`);
              }
            } catch (error) {
              console.warn(`[Cleaner] ?? Error al eliminar archivo de S3 (${element.src}):`, error);
            }
          }
        });

        await Promise.all(deletePromises);
      }
    } catch (error) {
      console.error(`Error cleaning up S3 content for lesson ${lessonId}:`, error);
    }
  };

  // ---------- Eliminar lección ----------
  const handleDeleteLesson = (moduleId, lessonId) => {
    if (!course) return;

    const modulo = course.modulos.find((m) => m.id === moduleId);
    const leccion = modulo?.lecciones?.find((l) => l.id === lessonId);
    const nombreLeccion = leccion?.titulo || "esta lección";

    setDeleteLessonDialog({
      open: true,
      moduleId,
      lessonId,
      lessonName: nombreLeccion,
    });
  };

  const confirmDeleteLesson = async () => {
    if (
      !course ||
      !deleteLessonDialog.moduleId ||
      !deleteLessonDialog.lessonId
    )
      return;

    try {
      // 1. Limpiar recursos de S3 asociados
      await deleteLessonContentFromS3(deleteLessonDialog.lessonId);

      // 2. Eliminar lección de BD
      await axios.delete(
        LMS_ENDPOINTS.leccionesById(deleteLessonDialog.lessonId)
      );

      console.log("? Lección eliminada exitosamente de BD");

      // 3. Eliminar el registro de ModuloContenido para que no aparezca en el curriculum ni navegacion
      try {
        const respModuloContenido = await axios.get(LMS_ENDPOINTS.moduloContenido);
        const todoModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);

        const registroContenido = todoModuloContenido.find(
          (mc) => mc.idModulo === deleteLessonDialog.moduleId &&
            mc.idAsociado === deleteLessonDialog.lessonId &&
            mc.tablaContenido === "Lecciones"
        );

        if (registroContenido) {
          await axios.delete(
            LMS_ENDPOINTS.moduloContenidoById(registroContenido.idModuloContenido)
          );
          console.log("? Registro de ModuloContenido para la lección eliminado");
        }
      } catch (mcError) {
        console.warn("⚠️ No se pudo eliminar el registro de ModuloContenido:", mcError);
      }

      // 3. Recargar para sincronizar estructura y duracion
      await loadCursoCompleto();
    } catch (error) {
      console.error("? Error al eliminar lección:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudo eliminar la lección de la base de datos.",
        variant: "error"
      });
    }

    setDeleteLessonDialog({
      open: false,
      moduleId: null,
      lessonId: null,
      lessonName: "",
    });
  };

  // ---------- Eliminar módulo ----------
  const handleDeleteModule = (moduleId) => {
    if (!course) return;
    const modulo = course.modulos.find((m) => m.id === moduleId);
    const nombre = modulo?.titulo || "este módulo";

    setDeleteModuleDialog({
      open: true,
      moduleId,
      moduleName: nombre,
    });
  };

  const confirmDeleteModule = async () => {
    if (!course || !deleteModuleDialog.moduleId) return;

    try {
      // 1. Buscar el módulo para obtener sus lecciones
      const moduloToDelete = course.modulos.find(m => m.id === deleteModuleDialog.moduleId);

      if (moduloToDelete && moduloToDelete.contenido) {
        // Filtrar solo las lecciones
        const lecciones = moduloToDelete.contenido.filter(c => c.tipo === "LECCION");

        if (lecciones.length > 0) {
          console.log(`[Module Delete] Limpiando S3 para ${lecciones.length} lecciones del módulo...`);
          // Ejecutar limpieza para cada lección
          const cleanupPromises = lecciones.map(leccion =>
            deleteLessonContentFromS3(leccion.idLeccion || leccion.id)
          );
          await Promise.all(cleanupPromises);
        }
      }

      // 2. Eliminar módulo de BD
      await axios.delete(
        LMS_ENDPOINTS.moduloById(deleteModuleDialog.moduleId)
      );

      console.log("? Módulo eliminado exitosamente de BD");

      // 3. Recargar para sincronizar estructura y duracion
      await loadCursoCompleto();
    } catch (error) {
      console.error("? Error al eliminar módulo:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudo eliminar el módulo de la base de datos.",
        variant: "error"
      });
    }

    setDeleteModuleDialog({
      open: false,
      moduleId: null,
      moduleName: "",
    });
  };

  // ---------- Eliminar cuestionario ----------
  const handleDeleteQuiz = (moduleId, quizId) => {
    if (!course) return;

    const modulo = course.modulos.find((m) => m.id === moduleId);
    const quiz = modulo?.contenido?.find((c) => c.tipo === "CUESTIONARIO" && c.id === quizId);
    const nombreQuiz = quiz?.titulo || "este cuestionario";

    setDeleteQuizDialog({
      open: true,
      moduleId,
      quizId,
      quizName: nombreQuiz,
    });
  };

  const confirmDeleteQuiz = async () => {
    if (
      !course ||
      !deleteQuizDialog.moduleId ||
      !deleteQuizDialog.quizId
    )
      return;

    try {
      // 1. Eliminar el cuestionario
      await axios.delete(
        LMS_ENDPOINTS.cuestionarioById(deleteQuizDialog.quizId)
      );

      console.log("? Cuestionario eliminado exitosamente de BD");

      // 2. Eliminar el registro de ModuloContenido
      // Primero necesitamos encontrar el idModuloContenido
      const respModuloContenido = await axios.get(LMS_ENDPOINTS.moduloContenido);
      const todoModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);

      const registroContenido = todoModuloContenido.find(
        (mc) => mc.idModulo === deleteQuizDialog.moduleId &&
          mc.idAsociado === deleteQuizDialog.quizId &&
          mc.tablaContenido === "Cuestionario"
      );

      if (registroContenido) {
        await axios.delete(
          LMS_ENDPOINTS.moduloContenidoById(registroContenido.idModuloContenido)
        );
        console.log("? Registro de ModuloContenido eliminado");
      }

      // 3. Recargar el curso
      await loadCursoCompleto();
    } catch (error) {
      console.error("? Error al eliminar cuestionario:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudo eliminar el cuestionario de la base de datos.",
        variant: "error"
      });
    }

    setDeleteQuizDialog({
      open: false,
      moduleId: null,
      quizId: null,
      quizName: "",
    });
  };

  // ---------- Edición: título de examen ----------

  const startEditExamTitle = (moduleId, examId, currentTitle) => {
    setEditingLesson(null);
    setEditingQuiz(null);
    setEditingExam({ moduleId, examId });
    setExamTitleDraft(currentTitle);
  };

  const saveExamTitle = async () => {
    if (!editingExam || !course) return;

    const trimmed = examTitleDraft.trim();
    if (!trimmed) {
      setAlertDialog({
        open: true,
        title: "Campo requerido",
        message: "El título del examen no puede estar vacío.",
        variant: "warning"
      });
      return;
    }

    const modulo = course.modulos.find(
      (m) => m.id === editingExam.moduleId
    );
    const exam = modulo?.contenido?.find(
      (c) => c.tipo === "EXAMEN" && c.id === editingExam.examId
    );

    if (!exam) {
      setEditingExam(null);
      return;
    }

    if (exam.titulo === trimmed) {
      setEditingExam(null);
      return;
    }

    // Actualizar estado local primero para ver el cambio inmediatamente
    const updated = {
      ...course,
      modulos: course.modulos.map((m) => {
        if (m.id !== editingExam.moduleId) return m;
        return {
          ...m,
          contenido: m.contenido.map((c) =>
            c.tipo === "EXAMEN" && c.id === editingExam.examId
              ? { ...c, titulo: trimmed }
              : c
          ),
        };
      }),
    };
    setCourse(updated);
    setEditingExam(null);

    // Luego guardar en BD
    try {
      const examDTO = {
        idExamen: exam.idExamen,
        idCurso: course.idCurso,
        descripcion: exam.descripcion || "descripcion",
        nombre: trimmed,
        limiteIntentos: exam.limiteIntentos || 3,
        activo: exam.activo !== undefined ? exam.activo : true,
      };

      await axios.put(
        LMS_ENDPOINTS.examen,
        examDTO
      );

      console.log("? Título del examen actualizado en BD");
    } catch (error) {
      console.error("? Error al actualizar título del examen:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudieron guardar los cambios del examen en la base de datos.",
        variant: "error"
      });
      // Recargar en caso de error
      await loadCursoCompleto();
    }
  };

  const handleExamTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveExamTitle();
    } else if (e.key === "Escape") {
      setEditingExam(null);
    }
  };

  // ---------- Eliminar examen ----------
  const handleDeleteExam = (moduleId, examId) => {
    if (!course) return;

    const modulo = course.modulos.find((m) => m.id === moduleId);
    const exam = modulo?.contenido?.find((c) => c.tipo === "EXAMEN" && c.id === examId);
    const nombreExam = exam?.titulo || "este examen";

    setDeleteExamDialog({
      open: true,
      moduleId,
      examId,
      examName: nombreExam,
    });
  };

  const confirmDeleteExam = async () => {
    if (
      !course ||
      !deleteExamDialog.moduleId ||
      !deleteExamDialog.examId
    )
      return;

    try {
      // 1. Eliminar el examen
      await axios.delete(
        LMS_ENDPOINTS.examenById(deleteExamDialog.examId)
      );

      console.log("? Examen eliminado exitosamente de BD");

      // 2. Eliminar el registro de ModuloContenido
      const respModuloContenido = await axios.get(LMS_ENDPOINTS.moduloContenido);
      const todoModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);

      const registroContenido = todoModuloContenido.find(
        (mc) => mc.idModulo === deleteExamDialog.moduleId &&
          mc.idAsociado === deleteExamDialog.examId &&
          mc.tablaContenido === "Examen"
      );

      if (registroContenido) {
        await axios.delete(
          LMS_ENDPOINTS.moduloContenidoById(registroContenido.idModuloContenido)
        );
        console.log("? Registro de ModuloContenido eliminado");
      }

      // 3. Recargar el curso
      await loadCursoCompleto();
    } catch (error) {
      console.error("? Error al eliminar examen:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudo eliminar el examen de la base de datos.",
        variant: "error"
      });
    }

    setDeleteExamDialog({
      open: false,
      moduleId: null,
      examId: null,
      examName: "",
    });
  };

  // ---------- Drag & Drop para reordenar contenido ----------
  const handleDragStart = (e, moduleId, item, index) => {
    e.stopPropagation();
    console.log(`[DnD] Started dragging item: ${item.id} (Module: ${moduleId})`);
    setDraggedItem({ moduleId, item, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  // Estado para el target del drag (donde se va a soltar)
  const [dragOverTarget, setDragOverTarget] = useState(null); // { moduleId, index }

  const handleDragOver = (e, moduleId, index) => {
    e.preventDefault();
    e.stopPropagation(); // Importante para evitar conflicto con el contenedor del módulo
    e.dataTransfer.dropEffect = 'move';

    // Solo actualizar si es diferente para evitar re-renders innecesarios
    if (
      !dragOverTarget ||
      dragOverTarget.moduleId !== moduleId ||
      dragOverTarget.index !== index
    ) {
      console.log(`[DnD] Drag over target: Module ${moduleId}, Index ${index}`);
      setDragOverTarget({ moduleId, index });
    }
  };

  const handleDrop = async (e, moduleId, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);

    if (!draggedItem) {
      console.warn('Drop attempt but no draggedItem found');
      return;
    }

    const sourceModuleId = draggedItem.moduleId;
    const targetModuleId = moduleId;
    const dragIndex = draggedItem.index;

    console.log(`[Drop] Source: ${sourceModuleId}, Target: ${targetModuleId}, DragIdx: ${dragIndex}, DropIdx: ${dropIndex}`);

    // Verificar si es el mismo módulo (convirtiendo a String para evitar errores de tipo)
    if (String(sourceModuleId) === String(targetModuleId)) {
      if (dragIndex === dropIndex) {
        setDraggedItem(null);
        return;
      }

      // Buscar módulo usando conversión a String para seguridad
      const modulo = course.modulos.find(m => String(m.idModulo) === String(moduleId));
      if (!modulo) {
        console.error(`? Módulo destino ${moduleId} no encontrado en la lista.`);
        return;
      }

      const contenido = [...(modulo.contenido || [])];
      const [removed] = contenido.splice(dragIndex, 1);
      contenido.splice(dropIndex, 0, removed);

      const contenidoConOrden = contenido.map((item, idx) => ({ ...item, orden: idx + 1 }));

      setCourse(prev => ({
        ...prev,
        modulos: prev.modulos.map(m =>
          String(m.idModulo) === String(moduleId)
            ? { ...m, contenido: contenidoConOrden }
            : m
        )
      }));

      try {
        const updatePromises = contenido.map((item, index) => {
          const nuevoOrden = index + 1;
          const payload = {
            IdModuloContenido: item.idModuloContenido,
            IdModulo: Number(moduleId),
            IdAsociado: item.tipo === "LECCION"
              ? item.idLeccion
              : item.tipo === "CUESTIONARIO"
                ? item.idCuestionario
                : item.idExamen,
            TablaContenido: item.tipo === "LECCION"
              ? "Lecciones"
              : item.tipo === "CUESTIONARIO"
                ? "Cuestionario"
                : "Examen",
            Activo: item.activo !== undefined ? item.activo : true,
            Orden: nuevoOrden
          };
          return axios.put(LMS_ENDPOINTS.moduloContenido, payload);
        });

        await Promise.all(updatePromises);
        console.log("✅ Orden actualizado en BD (mismo módulo)");
        await loadCursoCompleto(false);

      } catch (error) {
        console.error("❌ Error al actualizar orden:", error);
        setAlertDialog({
          open: true,
          title: "Error de ordenamiento",
          message: "No se pudo actualizar el orden del contenido. Recargando...",
          variant: "error"
        });
        await loadCursoCompleto(true);
      }

    } else {
      // Movimiento entre módulos DIFERENTES
      console.log(`?? Moviendo item entre módulos: ${sourceModuleId} -> ${targetModuleId}`);

      const sourceModule = course.modulos.find(m => String(m.idModulo) === String(sourceModuleId));
      const targetModule = course.modulos.find(m => String(m.idModulo) === String(targetModuleId));

      if (!sourceModule || !targetModule) {
        console.error('? Módulo origen o destino no encontrado para movimiento cruzado.');
        setDraggedItem(null);
        return;
      }

      const sourceContent = [...(sourceModule.contenido || [])];
      const targetContent = [...(targetModule.contenido || [])];

      // Remover del origen
      const [movedItem] = sourceContent.splice(dragIndex, 1);

      // Insertar en destino
      targetContent.splice(dropIndex, 0, movedItem);

      const sourceContentWithOrder = sourceContent.map((item, idx) => ({ ...item, orden: idx + 1 }));
      const targetContentWithOrder = targetContent.map((item, idx) => ({ ...item, orden: idx + 1 }));

      // Actualización optimista
      setCourse(prev => ({
        ...prev,
        modulos: prev.modulos.map(m => {
          if (String(m.idModulo) === String(sourceModuleId)) {
            return { ...m, contenido: sourceContentWithOrder };
          }
          if (String(m.idModulo) === String(targetModuleId)) {
            return { ...m, contenido: targetContentWithOrder };
          }
          return m;
        })
      }));

      try {
        // 1. Actualizar módulo origen (reordenar restantes)
        const sourceUpdates = sourceContent.map((item, index) => {
          const nuevoOrden = index + 1;
          return axios.put(LMS_ENDPOINTS.moduloContenido, {
            IdModuloContenido: item.idModuloContenido,
            IdModulo: Number(sourceModuleId),
            IdAsociado: item.tipo === "LECCION" ? item.idLeccion : (item.tipo === "CUESTIONARIO" ? item.idCuestionario : item.idExamen),
            TablaContenido: item.tipo === "LECCION" ? "Lecciones" : (item.tipo === "CUESTIONARIO" ? "Cuestionario" : "Examen"),
            Activo: item.activo !== undefined ? item.activo : true,
            Orden: nuevoOrden
          });
        });

        // 2. Actualizar módulo destino (reordenar + actualizar IdModulo)
        const targetUpdates = targetContent.map((item, index) => {
          const nuevoOrden = index + 1;
          return axios.put(LMS_ENDPOINTS.moduloContenido, {
            IdModuloContenido: item.idModuloContenido,
            IdModulo: Number(targetModuleId),
            IdAsociado: item.tipo === "LECCION" ? item.idLeccion : (item.tipo === "CUESTIONARIO" ? item.idCuestionario : item.idExamen),
            TablaContenido: item.tipo === "LECCION" ? "Lecciones" : (item.tipo === "CUESTIONARIO" ? "Cuestionario" : "Examen"),
            Activo: item.activo !== undefined ? item.activo : true,
            Orden: nuevoOrden
          });
        });

        await Promise.all([...sourceUpdates, ...targetUpdates]);
        console.log("✅ Movimiento entre módulos completado en BD");
        await loadCursoCompleto(false); // Silent reload

      } catch (error) {
        console.error("❌ Error al mover contenido entre módulos:", error);
        setAlertDialog({
          open: true,
          title: "Error de movimiento",
          message: "No se pudo mover el contenido entre módulos. Recargando...",
          variant: "error"
        });
        await loadCursoCompleto(true); // Full reload with loading indicator
      }
    }

    setDraggedItem(null);
  };


  // ---------- Drag & Drop para reordenar MÓDULOS ----------
  const handleModuleDragStart = (e, modulo, index) => {
    console.log(`[DnD Module] Attempting to drag module ${modulo.id}. Handle active: ${isDragHandleActive}`);
    // Solo permitir drag si se activa desde el handle (círculo)
    if (!isDragHandleActive) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    setDraggedModule({ modulo, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleModuleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragOverModuleIndex !== index) {
      setDragOverModuleIndex(index);
    }
  };

  const handleModuleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverModuleIndex(null);
    setIsDragHandleActive(false);

    // Soporte para soltar un item en un módulo (se agrega al final)
    if (draggedItem) {
      const targetModule = course.modulos[dropIndex];
      if (targetModule) {
        const newIndex = (targetModule.contenido || []).length;
        await handleDrop(e, targetModule.idModulo, newIndex);
      }
      return;
    }

    if (!draggedModule) return;

    const dragIndex = draggedModule.index;
    if (dragIndex === dropIndex) {
      setDraggedModule(null);
      return;
    }

    // Reordenar localmente
    const newModulos = [...(course.modulos || [])];
    const [removed] = newModulos.splice(dragIndex, 1);
    newModulos.splice(dropIndex, 0, removed);

    // Actualizar propiedad 'orden' en los objetos para consistencia
    const modulosActualizados = newModulos.map((m, idx) => ({
      ...m,
      orden: idx + 1
    }));

    // Actualización optimista
    const updatedCourse = { ...course, modulos: modulosActualizados };
    setCourse(updatedCourse);
    setDraggedModule(null);

    // Actualizar orden en BD
    try {
      const updatePromises = modulosActualizados.map((mod) => {
        const moduloDTO = {
          IdModulo: mod.idModulo,
          IdCurso: mod.idCurso,
          Nombre: mod.nombre || mod.titulo || "",
          Orden: mod.orden,
          Activo: mod.activo !== undefined ? mod.activo : true,
        };

        // Usamos PUT al endpoint de módulo
        return axios.put(LMS_ENDPOINTS.modulo, moduloDTO);
      });

      await Promise.all(updatePromises);
      console.log("? Orden de módulos actualizado en BD");
    } catch (error) {
      console.error("? Error al actualizar orden de módulos:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudo actualizar el orden de los módulos.",
        variant: "error"
      });
      // Revertir cambios recargando
      loadCursoCompleto();
    }
  };

  // ---------- Agregar módulo ----------
  const handleAddModule = async () => {
    if (!course || !course.idCurso || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const index = (course.modulos || []).length + 1;

    try {
      const moduloDTO = {
        IdCurso: course.idCurso,
        Nombre: `Módulo ${index}: Nuevo módulo`,
        Orden: index,
        Activo: true,
      };

      const response = await axios.post(
        LMS_ENDPOINTS.modulo,
        moduloDTO
      );

      if (!response.data) {
        setAlertDialog({
          open: true,
          title: "Error",
          message: "No se pudo crear el módulo en la base de datos",
          variant: "error"
        });
        return;
      }

      const nuevoModuloId =
        response.data.idModulo || response.data.IdModulo;

      console.log(`? Módulo creado en BD con ID: ${nuevoModuloId}`);

      await loadCursoCompleto();

      setExpandedModules((prev) => ({
        ...prev,
        [nuevoModuloId]: true,
      }));
    } catch (error) {
      console.error("? Error al crear módulo:", error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudo crear el módulo en la base de datos.",
        variant: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };



  // ---------- Modal NUEVO (Lección / Cuestionario / Evaluación) ----------
  const openNewContentModal = (moduleId) => {
    setNewContentModal({ open: true, moduleId });
  };

  const closeNewContentModal = () => {
    setNewContentModal({ open: false, moduleId: null });
  };

  const handleCreateContent = async (kind) => {
    if (!course || !newContentModal.moduleId || isSubmitting) return;

    setIsSubmitting(true);
    const moduleId = newContentModal.moduleId;
    const modulo = course.modulos.find(
      (m) => m.idModulo === moduleId
    );

    if (!modulo) {
      setAlertDialog({
        open: true,
        title: "Error",
        message: "Módulo no encontrado",
        variant: "error"
      });
      return;
    }

    try {
      if (kind === "LECCION") {
        const existing = modulo.contenido || [];
        const countLessons = existing.filter(
          (x) => x.tipo === "LECCION"
        ).length;

        // Calcular el siguiente orden
        const maxOrden = existing.length > 0
          ? Math.max(...existing.map(c => c.idModuloContenido || 0))
          : 0;
        const nuevoOrden = maxOrden + 1;

        // Obtener monedas configuradas del curso
        // Buscar en las lecciones existentes del módulo actual
        let monedasParaLeccion = 0;
        const leccionesExistentes = existing.filter(x => x.tipo === "LECCION");

        if (leccionesExistentes.length > 0) {
          // Si ya hay lecciones, usar las monedas de la primera
          monedasParaLeccion = leccionesExistentes[0].monedas || 0;
        } else {
          // Si no hay lecciones en este módulo, buscar en otros módulos del curso
          const todasLasLecciones = course.modulos
            .flatMap(m => (m.contenido || []).filter(c => c.tipo === "LECCION"));

          if (todasLasLecciones.length > 0) {
            monedasParaLeccion = todasLasLecciones[0].monedas || 0;
          }
        }

        // 1. Crear lección
        const leccionDTO = {
          IdModulo: moduleId,
          Titulo: `Nueva lección ${countLessons + 1}`,
          Monedas: monedasParaLeccion,
          Activo: true,
        };

        const response = await axios.post(
          LMS_ENDPOINTS.lecciones,
          leccionDTO
        );

        if (!response.data) {
          setAlertDialog({
            open: true,
            title: "Error",
            message: "No se pudo crear la lección en la base de datos",
            variant: "error"
          });
          return;
        }

        const nuevaLeccionId = response.data;
        console.log(
          `? Lección creada en BD con ID: ${nuevaLeccionId}`
        );

        // 2. Crear registro en ModuloContenido con orden
        const moduloContenidoDTO = {
          IdModuloContenido: 0,
          IdModulo: moduleId,
          IdAsociado: nuevaLeccionId,
          TablaContenido: "Lecciones",
          Activo: true,
          Orden: nuevoOrden
        };

        await axios.post(LMS_ENDPOINTS.moduloContenido, moduloContenidoDTO);
        console.log(`? Registro en ModuloContenido creado con orden: ${nuevoOrden}`);

        // 3. Recargar curso
        await loadCursoCompleto();
        closeNewContentModal();

      } else if (kind === "CUESTIONARIO") {
        const existing = modulo.contenido || [];
        const countQuizzes = existing.filter(
          (x) => x.tipo === "CUESTIONARIO"
        ).length;

        // Calcular el siguiente orden
        const maxOrden = existing.length > 0
          ? Math.max(...existing.map(c => c.idModuloContenido || 0))
          : 0;
        const nuevoOrden = maxOrden + 1;

        const nombreBase = `Cuestionario ${countQuizzes + 1}`;

        // 1. Crear cuestionario
        const cuestionarioDTO = {
          idCurso: course.idCurso,
          nombre:
            course.titulo && course.titulo.length > 0
              ? `${course.titulo} - ${nombreBase}`
              : nombreBase,
        };

        console.log("?? Creando cuestionario:", cuestionarioDTO);

        const response = await axios.post(
          LMS_ENDPOINTS.cuestionario,
          cuestionarioDTO
        );

        console.log("?? Respuesta del servidor:", response.data);

        if (!response.data) {
          setAlertDialog({
            open: true,
            title: "Error",
            message: "No se pudo crear el cuestionario en la base de datos",
            variant: "error"
          });
          return;
        }

        const nuevoCuestionarioId = response.data;
        console.log(
          `? Cuestionario creado en BD con ID: ${nuevoCuestionarioId}`
        );

        // 2. Crear registro en ModuloContenido con orden
        const moduloContenidoDTO = {
          IdModuloContenido: 0,
          IdModulo: moduleId,
          IdAsociado: nuevoCuestionarioId,
          TablaContenido: "Cuestionario",
          Activo: true,
          Orden: nuevoOrden
        };

        await axios.post(LMS_ENDPOINTS.moduloContenido, moduloContenidoDTO);
        console.log(`? Registro en ModuloContenido creado con orden: ${nuevoOrden}`);

        // Recargar curso para mostrar el nuevo item
        await loadCursoCompleto();
        closeNewContentModal();
        // navigate(
        //   `/menu/lms/cuestionario/${nuevoCuestionarioId}/preguntas`
        // );

      } else if (kind === "EVALUACION") {
        const existing = modulo.contenido || [];
        const countExams = existing.filter(
          (x) => x.tipo === "EXAMEN"
        ).length;

        // Calcular el siguiente orden
        const maxOrden = existing.length > 0
          ? Math.max(...existing.map(c => c.idModuloContenido || 0))
          : 0;
        const nuevoOrden = maxOrden + 1;

        const nombreBase = `Evaluación ${countExams + 1}`;

        // 1. Crear examen
        const examenDTO = {
          idExamen: 0,
          idCurso: course.idCurso,
          descripcion: "descripcion",
          nombre:
            course.titulo && course.titulo.length > 0
              ? `${course.titulo} - ${nombreBase}`
              : nombreBase,
          limiteIntentos: 3,
          activo: true
        };

        console.log("?? Creando examen:", examenDTO);

        const response = await axios.post(
          LMS_ENDPOINTS.examen,
          examenDTO
        );

        console.log("?? Respuesta del servidor:", response.data);

        if (!response.data) {
          setAlertDialog({
            open: true,
            title: "Error",
            message: "No se pudo crear el examen en la base de datos",
            variant: "error"
          });
          return;
        }

        const nuevoExamenId = response.data;
        console.log(
          `? Examen creado en BD con ID: ${nuevoExamenId}`
        );

        // 2. Crear registro en ModuloContenido con orden
        const moduloContenidoExamenDTO = {
          IdModuloContenido: 0,
          IdModulo: moduleId,
          IdAsociado: nuevoExamenId,
          TablaContenido: "Examen",
          Activo: true,
          Orden: nuevoOrden
        };

        await axios.post(LMS_ENDPOINTS.moduloContenido, moduloContenidoExamenDTO);
        console.log(`? Registro en ModuloContenido creado con orden: ${nuevoOrden}`);

        // Recargar curso para mostrar el nuevo item
        await loadCursoCompleto();
        closeNewContentModal();
        // navigate(
        //   `/menu/lms/examen/${nuevoExamenId}/preguntas`
        // );
      }
    } catch (error) {
      console.error(`? Error al crear ${kind}:`, error);
      setAlertDialog({
        open: true,
        title: "Error",
        message: `No se pudo crear el contenido en la base de datos.`,
        variant: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------- Edición: título del curso ----------
  const startEditCourseTitle = () => {
    if (!course) return;
    setCourseTitleDraft(course.titulo || "");
    setEditingCourseTitle(true);
  };

  const cancelEditCourseTitle = () => {
    setEditingCourseTitle(false);
    setCourseTitleDraft("");
  };

  const saveCourseTitle = () => {
    if (!course) return;
    const text = courseTitleDraft.trim();
    if (!text) {
      setAlertDialog({
        open: true,
        title: "Campo requerido",
        message: "El título del curso no puede estar vacío.",
        variant: "warning"
      });
      return;
    }
    const updated = { ...course, titulo: text, nombre: text };
    persistCourse(updated);
    setEditingCourseTitle(false);
  };

  const handleCourseTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveCourseTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditCourseTitle();
    }
  };

  // ---------- Edición: título del módulo ----------
  const startEditModuleTitle = (moduleId, currentTitle) => {
    setEditingModuleId(moduleId);
    setModuleTitleDraft(currentTitle || "");
  };

  const cancelEditModuleTitle = () => {
    setEditingModuleId(null);
    setModuleTitleDraft("");
  };

  const saveModuleTitle = async () => {
    if (!course || editingModuleId == null) {
      cancelEditModuleTitle();
      return;
    }
    const text = moduleTitleDraft.trim();
    if (!text) {
      cancelEditModuleTitle();
      return;
    }

    const moduloAActualizar = course.modulos.find(
      (m) => m.id === editingModuleId
    );
    if (!moduloAActualizar) {
      cancelEditModuleTitle();
      return;
    }

    const updated = {
      ...course,
      modulos: course.modulos.map((m) =>
        m.id === editingModuleId
          ? { ...m, titulo: text, nombre: text }
          : m
      ),
    };
    setCourse(updated);
    setEditingModuleId(null);

    await persistModule({
      ...moduloAActualizar,
      titulo: text,
      nombre: text,
    });
  };

  const handleModuleTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveModuleTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditModuleTitle();
    }
  };

  // ---------- Edición: título de la lección ----------
  const startEditLessonTitle = (
    moduleId,
    lessonId,
    currentTitle
  ) => {
    setEditingQuiz(null);
    setEditingExam(null);
    setEditingLesson({ moduleId, lessonId });
    setLessonTitleDraft(currentTitle || "");
  };

  const cancelEditLessonTitle = () => {
    setEditingLesson(null);
    setLessonTitleDraft("");
  };

  const saveLessonTitle = async () => {
    if (!course || !editingLesson) {
      cancelEditLessonTitle();
      return;
    }
    const text = lessonTitleDraft.trim();
    if (!text) {
      cancelEditLessonTitle();
      return;
    }

    const { moduleId, lessonId } = editingLesson;

    const modulo = course.modulos.find((m) => m.id === moduleId);
    const leccionAActualizar = modulo?.contenido?.find(
      (c) => c.tipo === "LECCION" && c.id === lessonId
    );

    if (!leccionAActualizar) {
      cancelEditLessonTitle();
      return;
    }

    // Actualizar estado local primero para ver el cambio inmediatamente
    const updated = {
      ...course,
      modulos: course.modulos.map((m) => {
        if (m.id !== moduleId) return m;
        return {
          ...m,
          contenido: m.contenido.map((c) =>
            c.tipo === "LECCION" && c.id === lessonId
              ? { ...c, titulo: text }
              : c
          ),
        };
      }),
    };
    setCourse(updated);
    setEditingLesson(null);

    // Luego guardar en BD
    await persistLesson({
      ...leccionAActualizar,
      titulo: text,
    });
  };

  const handleLessonTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveLessonTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditLessonTitle();
    }
  };

  // ========= RENDER =========
  if (loading) {
    return (
      <div className="cc-page">
        <p>Cargando curso...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cc-page">
        <p style={{ color: "red" }}>
          Error al cargar el curso.
        </p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="cc-page">
        <p>No se encontró el curso.</p>
      </div>
    );
  }



  return (
    <div className="cc-page">
      {/* Breadcrumb */}
      <div className="cc-breadcrumb">
        <button
          type="button"
          className="cc-breadcrumb-back"
          onClick={() =>
            navigate("/menu/lms/administrar-cursos")
          }
        >
          <FiArrowLeft />
          Volver a Administrar Cursos
        </button>
      </div>

      <div className="cc-layout">
        {/* MAIN */}
        <div className="cc-main">
          {/* Título de curso editable */}
          {editingCourseTitle ? (
            <input
              className="cc-inline-input cc-inline-input-course"
              value={courseTitleDraft}
              onChange={(e) =>
                setCourseTitleDraft(e.target.value)
              }
              onBlur={saveCourseTitle}
              onKeyDown={handleCourseTitleKeyDown}
              autoFocus
            />
          ) : (
            <h1
              className="cc-course-title"
              onDoubleClick={!course.terminado && !hasCertificate ? startEditCourseTitle : undefined}
              title={!course.terminado && !hasCertificate ? "Doble clic para editar el título del curso" : "Curso bloqueado - No se puede editar"}
              style={{
                cursor: !course.terminado && !hasCertificate ? 'text' : 'default',
                opacity: course.terminado ? 0.9 : 1
              }}
            >
              {course.titulo}
              {course.terminado && <span style={{ marginLeft: '0.5rem', fontSize: '0.8em', color: '#10b981' }}>✓ Publicado</span>}
            </h1>
          )}

          {/* Chips resumen */}
          <div className="cc-chip-row">
            <span className="cc-chip">
              {stats.totalModules} módulos
            </span>
            <span className="cc-chip">
              {stats.totalLessons} lecciones
            </span>
            <span className="cc-chip">
              {stats.durationWeeks || 0} semanas
            </span>
            <span className="cc-chip">
              {stats.cuestionarios} cuestionarios
            </span>
          </div>

          {/* Toolbar */}
          <div className="cc-toolbar">
            <button
              type="button"
              className="cc-add-lesson-btn"
              onClick={handleToggleAllModules}
            >
              {allExpanded
                ? "Contraer todas las secciones"
                : "Expandir todas las secciones"}
            </button>
          </div>

          {/* Lista de módulos */}
          <div className="cc-modules-list">
            {(course.modulos || []).map((modulo, index) => {
              const isExpanded = expandedModules[modulo.id];
              const contentCount = (modulo.contenido || []).length;

              const isEditingThisModule =
                editingModuleId === modulo.id;

              return (
                <div
                  key={modulo.id}
                  className={`cc-module-card ${dragOverModuleIndex === index ? 'cc-drag-over-module' : ''} ${draggedModule?.modulo?.id === modulo.id ? 'cc-dragging-module' : ''}`}
                  draggable={!course.terminado && !hasCertificate}
                  onDragStart={!course.terminado && !hasCertificate ? (e) => handleModuleDragStart(e, modulo, index) : undefined}
                  onDragOver={!course.terminado && !hasCertificate ? (e) => handleModuleDragOver(e, index) : undefined}
                  onDrop={!course.terminado && !hasCertificate ? (e) => handleModuleDrop(e, index) : undefined}
                >
                  {/* Header módulo */}
                  <div className="cc-module-header">
                    <div className="cc-module-header-left">
                      <div
                        className="cc-module-index-badge"
                        onMouseDown={() => setIsDragHandleActive(true)}
                        onMouseUp={() => setIsDragHandleActive(false)}
                        onMouseLeave={() => setIsDragHandleActive(false)}
                        style={{ cursor: !course.terminado && !hasCertificate ? 'grab' : 'default' }}
                        title={!course.terminado && !hasCertificate ? "Arrastra desde aquí para reordenar" : ""}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <div
                          className="cc-module-title-wrapper"
                          onDoubleClick={!course.terminado && !hasCertificate ? () =>
                            startEditModuleTitle(
                              modulo.id,
                              modulo.titulo
                            ) : undefined}
                        >
                          {isEditingThisModule ? (
                            <input
                              className="cc-inline-input"
                              value={moduleTitleDraft}
                              onChange={(e) =>
                                setModuleTitleDraft(
                                  e.target.value
                                )
                              }
                              onBlur={saveModuleTitle}
                              onKeyDown={
                                handleModuleTitleKeyDown
                              }
                              autoFocus
                            />
                          ) : (
                            <h2
                              className="cc-module-title"
                              title={!course.terminado && !hasCertificate ? "Doble clic para editar" : "No editable"}
                              style={{ cursor: !course.terminado && !hasCertificate ? 'text' : 'default' }}
                            >
                              {modulo.titulo}
                            </h2>
                          )}
                        </div>
                        <p className="cc-module-meta">
                          {contentCount}{" "}
                          {contentCount === 1
                            ? "elemento"
                            : "elementos"}
                        </p>
                      </div>
                    </div>

                    <div className="cc-module-header-right">
                      {!course.terminado && !hasCertificate && (
                        <>
                          <button
                            type="button"
                            className="cc-add-lesson-btn"
                            onClick={() =>
                              openNewContentModal(
                                modulo.idModulo
                              )
                            }
                            title="Agregar lección, cuestionario o evaluación"
                          >
                            + Nuevo
                          </button>
                          <button
                            type="button"
                            className="cc-lesson-btn cc-lesson-btn-delete"
                            onClick={() =>
                              handleDeleteModule(modulo.id)
                            }
                            title="Eliminar módulo"
                          >
                            <FiTrash2 />
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="cc-module-toggle-btn"
                        onClick={() =>
                          handleToggleModule(modulo.id)
                        }
                        title={
                          isExpanded
                            ? "Contraer"
                            : "Expandir"
                        }
                      >
                        {isExpanded ? (
                          <FiChevronUp />
                        ) : (
                          <FiChevronDown />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Contenido del módulo (si expandido) */}
                  {isExpanded && (
                    <div className="cc-lessons-list">
                      {(modulo.contenido || []).length === 0 && (
                        <p className="cc-empty-lessons">
                          No hay contenido. Presiona "+ Nuevo" para agregar.
                        </p>
                      )}

                      {(modulo.contenido || []).map(
                        (item, itemIndex) => {
                          const isEditingThisItem =
                            (item.tipo === "LECCION" &&
                              editingLesson?.moduleId === modulo.id &&
                              editingLesson?.lessonId === item.id) ||
                            (item.tipo === "CUESTIONARIO" &&
                              editingQuiz?.moduleId === modulo.id &&
                              editingQuiz?.quizId === item.id) ||
                            (item.tipo === "EXAMEN" &&
                              editingExam?.moduleId === modulo.id &&
                              editingExam?.examId === item.id);

                          // Determinar icono según tipo
                          const Icon = item.tipo === "LECCION"
                            ? FiFileText
                            : item.tipo === "CUESTIONARIO"
                              ? FiHelpCircle
                              : FiClipboard;

                          const isDragging = draggedItem?.item?.id === item.id;
                          const isDragOver = dragOverTarget?.moduleId === modulo.idModulo && dragOverTarget?.index === itemIndex;

                          return (
                            <div
                              key={`${item.tipo}-${item.id}`}
                              className={`cc-lesson-row ${item.completado ? "cc-lesson-completed" : ""
                                } ${item.tipo === "CUESTIONARIO" ? "cc-quiz-row" : ""} ${isDragging ? "cc-dragging" : ""} ${isDragOver ? "cc-drag-over" : ""}`}
                              draggable={!course.terminado && !hasCertificate}
                              onDragStart={!course.terminado && !hasCertificate ? (e) => handleDragStart(e, modulo.idModulo, item, itemIndex) : undefined}
                              onDragOver={!course.terminado && !hasCertificate ? (e) => handleDragOver(e, modulo.idModulo, itemIndex) : undefined}
                              onDrop={!course.terminado && !hasCertificate ? (e) => handleDrop(e, modulo.idModulo, itemIndex) : undefined}
                              style={{
                                cursor: !course.terminado && !hasCertificate ? 'move' : 'default',
                                opacity: isDragging ? 0.5 : 1
                              }}
                            >
                              {/* Main */}
                              <div
                                className="cc-lesson-main"
                                onClick={() =>
                                  item.tipo === "LECCION" && handleToggleLessonCompleted(
                                    modulo.id,
                                    item.id
                                  )
                                }
                              >
                                <div className="cc-lesson-icon">
                                  {item.completado ? <FiCheckCircle /> : <Icon />}
                                </div>
                                <div className="cc-lesson-texts">
                                  {isEditingThisItem && item.tipo === "LECCION" ? (
                                    <input
                                      className="cc-inline-input"
                                      value={
                                        lessonTitleDraft
                                      }
                                      onChange={(e) =>
                                        setLessonTitleDraft(
                                          e.target.value
                                        )
                                      }
                                      onBlur={
                                        saveLessonTitle
                                      }
                                      onKeyDown={
                                        handleLessonTitleKeyDown
                                      }
                                      autoFocus
                                      onClick={(e) =>
                                        e.stopPropagation()
                                      }
                                    />
                                  ) : isEditingThisItem && item.tipo === "CUESTIONARIO" ? (
                                    <input
                                      className="cc-inline-input"
                                      value={
                                        quizTitleDraft
                                      }
                                      onChange={(e) =>
                                        setQuizTitleDraft(
                                          e.target.value
                                        )
                                      }
                                      onBlur={
                                        saveQuizTitle
                                      }
                                      onKeyDown={
                                        handleQuizTitleKeyDown
                                      }
                                      autoFocus
                                      onClick={(e) =>
                                        e.stopPropagation()
                                      }
                                    />
                                  ) : isEditingThisItem && item.tipo === "EXAMEN" ? (
                                    <input
                                      className="cc-inline-input"
                                      value={
                                        examTitleDraft
                                      }
                                      onChange={(e) =>
                                        setExamTitleDraft(
                                          e.target.value
                                        )
                                      }
                                      onBlur={
                                        saveExamTitle
                                      }
                                      onKeyDown={
                                        handleExamTitleKeyDown
                                      }
                                      autoFocus
                                      onClick={(e) =>
                                        e.stopPropagation()
                                      }
                                    />
                                  ) : (
                                    <h3
                                      className="cc-lesson-title"
                                      onDoubleClick={!course.terminado && !hasCertificate ? (
                                        e
                                      ) => {
                                        e.stopPropagation();
                                        if (item.tipo === "LECCION") {
                                          startEditLessonTitle(
                                            modulo.id,
                                            item.id,
                                            item.titulo
                                          );
                                        } else if (item.tipo === "CUESTIONARIO") {
                                          startEditQuizTitle(
                                            modulo.id,
                                            item.id,
                                            item.titulo
                                          );
                                        } else if (item.tipo === "EXAMEN") {
                                          startEditExamTitle(
                                            modulo.id,
                                            item.id,
                                            item.titulo
                                          );
                                        }
                                      } : undefined}
                                      title={!course.terminado && !hasCertificate && (item.tipo === "LECCION" || item.tipo === "CUESTIONARIO" || item.tipo === "EXAMEN") ? "Doble clic para editar" : "No editable"}
                                      style={{ cursor: !course.terminado && !hasCertificate ? 'text' : 'default' }}
                                    >
                                      {item.titulo}
                                    </h3>
                                  )}
                                  {item.monedas > 0 && (
                                    <p className="cc-lesson-meta">
                                      🪙 {item.monedas}{" "}
                                      monedas
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="cc-lesson-actions">
                                {item.tipo === "LECCION" && (
                                  <>
                                    <button
                                      type="button"
                                      className="cc-lesson-btn cc-lesson-btn-ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const lessonId = item.idLeccion || item.id;
                                        navigate(`/menu/lms/curso-preview/${slug}/player/${lessonId}`);
                                      }}
                                      title="Ver lección (vista previa como estudiante)"
                                    >
                                      <FiEye /> Ver
                                    </button>
                                    <button
                                      type="button"
                                      className="cc-lesson-btn cc-lesson-btn-edit"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(
                                          "/menu/lms/lecciones",
                                          {
                                            state: {
                                              lessonId:
                                                item.idLeccion,
                                              hideNavigation:
                                                false,
                                              idCurso: course.idCurso
                                            },
                                          }
                                        );
                                      }}
                                      title="Editar lección"
                                    >
                                      <FiEdit2 /> Editar
                                    </button>
                                    {!course.terminado && !hasCertificate && (
                                      <button
                                        type="button"
                                        className="cc-lesson-btn cc-lesson-btn-delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteLesson(
                                            modulo.id,
                                            item.id
                                          );
                                        }}
                                        title="Eliminar lección"
                                      >
                                        <FiTrash2 />
                                      </button>
                                    )}
                                  </>
                                )}

                                {item.tipo === "CUESTIONARIO" && (
                                  <>
                                    <button
                                      type="button"
                                      className="cc-lesson-btn cc-lesson-btn-ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const quizId = item.idCuestionario || item.id;
                                        navigate(`/menu/lms/curso-preview/${slug}/player/${quizId}?type=quiz`);
                                      }}
                                      title="Ver cuestionario (vista previa)"
                                    >
                                      <FiEye /> Ver
                                    </button>
                                    <button
                                      type="button"
                                      className="cc-lesson-btn cc-lesson-btn-edit"
                                      onClick={() =>
                                        navigate(
                                          `/menu/lms/cuestionario/${item.idCuestionario}/preguntas`,
                                          { state: { idCurso: course.idCurso } }
                                        )
                                      }
                                      title="Editar cuestionario"
                                    >
                                      <FiEdit2 /> Editar
                                    </button>
                                    {!course.terminado && !hasCertificate && (
                                      <button
                                        type="button"
                                        className="cc-lesson-btn cc-lesson-btn-delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteQuiz(
                                            modulo.id,
                                            item.id
                                          );
                                        }}
                                        title="Eliminar cuestionario"
                                      >
                                        <FiTrash2 />
                                      </button>
                                    )}
                                  </>
                                )}

                                {item.tipo === "EXAMEN" && (
                                  <>
                                    <button
                                      type="button"
                                      className="cc-lesson-btn cc-lesson-btn-ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const examId = item.idExamen || item.id;
                                        navigate(`/menu/lms/curso-preview/${slug}/player/${examId}?type=exam`);
                                      }}
                                      title="Ver examen (vista previa)"
                                    >
                                      <FiEye /> Ver
                                    </button>
                                    <button
                                      type="button"
                                      className="cc-lesson-btn cc-lesson-btn-edit"
                                      onClick={() =>
                                        navigate(
                                          `/menu/lms/examen/${item.idExamen}/preguntas`,
                                          { state: { idCurso: course.idCurso } }
                                        )
                                      }
                                      title="Editar examen"
                                    >
                                      <FiEdit2 /> Editar
                                    </button>
                                    {!course.terminado && !hasCertificate && (
                                      <button
                                        type="button"
                                        className="cc-lesson-btn cc-lesson-btn-delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteExam(
                                            modulo.id,
                                            item.id
                                          );
                                        }}
                                        title="Eliminar examen"
                                      >
                                        <FiTrash2 />
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Botón agregar módulo - solo si no está terminado */}
          {!course.terminado && !hasCertificate && (
            <div className="cc-add-module-wrapper">
              <button
                type="button"
                className="cc-add-module-btn"
                onClick={handleAddModule}
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
              >
                {isSubmitting ? "Agregando..." : "+ Agregar nuevo módulo"}
              </button>
            </div>
          )}
        </div>

        {/* SIDEBAR / Resumen */}
        <aside className="cc-sidebar">
          <div className="cc-summary-card">
            <div className="cc-summary-status-pill">
              En curso
            </div>
            <h3 className="cc-summary-title">
              Resumen del curso
            </h3>

            <div className="cc-summary-grid">
              <div className="cc-summary-item">
                <small className="cc-summary-label">
                  Duración
                </small>
                <strong className="cc-summary-value">
                  {course.duracion || 0} días
                </strong>
              </div>
              <div className="cc-summary-item">
                <small className="cc-summary-label">
                  Módulos
                </small>
                <strong className="cc-summary-value">
                  {stats.totalModules}
                </strong>
              </div>
              <div className="cc-summary-item">
                <small className="cc-summary-label">
                  Lecciones
                </small>
                <strong className="cc-summary-value">
                  {stats.totalLessons}
                </strong>
              </div>
              <div className="cc-summary-item">
                <small className="cc-summary-label">
                  Cuestionarios
                </small>
                <strong className="cc-summary-value">
                  {stats.cuestionarios}
                </strong>
              </div>
            </div>



            {/* Botón principal */}
            <button
              type="button"
              className="cc-summary-btn-main"
              onClick={handleOpenConfigModal}
            >
              Editar Configuración del Curso
            </button>

            {/* Botón solicitar certificado / subir curso - solo si no está terminado */}
            {!course.terminado && (
              <button
                type="button"
                className="cc-summary-btn-main"
                onClick={handleRequestCertificate}
                style={{
                  marginTop: '0.75rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                {hasCertificate ? '🚀 Subir Curso' : '📜 Solicitar Certificado'}
              </button>
            )}
          </div>
        </aside>
      </div>

      {/* Dialogos de confirmación de eliminación */}
      <ConfirmDeleteDialog
        open={deleteModuleDialog.open}
        onClose={() =>
          setDeleteModuleDialog({
            open: false,
            moduleId: null,
            moduleName: "",
          })
        }
        onConfirm={confirmDeleteModule}
        roleName={deleteModuleDialog.moduleName}
      />

      <ConfirmDeleteDialog
        open={deleteLessonDialog.open}
        onClose={() =>
          setDeleteLessonDialog({
            open: false,
            moduleId: null,
            lessonId: null,
            lessonName: "",
          })
        }
        onConfirm={confirmDeleteLesson}
        roleName={deleteLessonDialog.lessonName}
      />

      <ConfirmDeleteDialog
        open={deleteQuizDialog.open}
        onClose={() =>
          setDeleteQuizDialog({
            open: false,
            moduleId: null,
            quizId: null,
            quizName: "",
          })
        }
        onConfirm={confirmDeleteQuiz}
        roleName={deleteQuizDialog.quizName}
      />

      <ConfirmDeleteDialog
        open={deleteExamDialog.open}
        onClose={() =>
          setDeleteExamDialog({
            open: false,
            moduleId: null,
            examId: null,
            examName: "",
          })
        }
        onConfirm={confirmDeleteExam}
        roleName={deleteExamDialog.examName}
      />

      {/* MODAL NUEVO CONTENIDO */}
      {newContentModal.open && (
        <div className="cc-course-modal-overlay">
          <div className="cc-course-modal cc-course-modal-small">
            <h3 className="cc-course-modal-title">
              Agregar contenido al módulo
            </h3>
            <p className="cc-course-modal-subtitle">
              Elige qué tipo de elemento quieres sumar a este
              módulo. Puedes mezclar lecciones, cuestionarios y
              evaluaciones.
            </p>

            <div className="cc-newcontent-grid">
              <button
                type="button"
                className="cc-newcontent-option"
                onClick={() => handleCreateContent("LECCION")}
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.7 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}
              >
                <div className="cc-newcontent-icon-wrapper">
                  <FiFileText className="cc-newcontent-icon" />
                </div>
                <div className="cc-newcontent-text">
                  <span className="cc-newcontent-title">
                    Lección
                  </span>
                  <span className="cc-newcontent-subtitle">
                    Contenido explicativo en video, texto o
                    recurso multimedia.
                  </span>
                </div>
              </button>

              <button
                type="button"
                className="cc-newcontent-option"
                onClick={() => handleCreateContent("CUESTIONARIO")}
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.7 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}
              >
                <div className="cc-newcontent-icon-wrapper">
                  <FiHelpCircle className="cc-newcontent-icon" />
                </div>
                <div className="cc-newcontent-text">
                  <span className="cc-newcontent-title">
                    Cuestionario
                  </span>
                  <span className="cc-newcontent-subtitle">
                    Quiz corto para reforzar conceptos (sin
                    valor final).
                  </span>
                </div>
              </button>

              <button
                type="button"
                className="cc-newcontent-option"
                onClick={() => handleCreateContent("EVALUACION")}
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.7 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}
              >
                <div className="cc-newcontent-icon-wrapper">
                  <FiClipboard className="cc-newcontent-icon" />
                </div>
                <div className="cc-newcontent-text">
                  <span className="cc-newcontent-title">
                    Evaluación
                  </span>
                  <span className="cc-newcontent-subtitle">
                    Examen con peso en la calificación del
                    curso.
                  </span>
                </div>
              </button>
            </div>

            <div className="cc-course-modal-actions cc-newcontent-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={closeNewContentModal}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIGURACIÓN DEL CURSO */}
      {configModalOpen && (
        <div className="cc-course-modal-overlay">
          <div className="cc-course-modal">
            <h3 className="cc-course-modal-title">Configuración del Curso</h3>
            <p className="cc-course-modal-subtitle">
              Ajusta la información del curso. Los cambios se aplicarán inmediatamente.
            </p>

            <div className="cc-course-modal-body">
              <div className="cc-course-modal-field">
                <label>Nombre del Curso</label>
                <input
                  type="text"
                  value={configDraft.nombre}
                  onChange={(e) =>
                    setConfigDraft((prev) => ({
                      ...prev,
                      nombre: e.target.value,
                    }))
                  }
                  placeholder="Ej. Seguridad Industrial Básica"
                  disabled={hasCertificate}
                  readOnly={hasCertificate}
                  style={{
                    backgroundColor: hasCertificate ? '#f3f4f6' : 'white',
                    cursor: hasCertificate ? 'not-allowed' : 'text',
                    opacity: hasCertificate ? 0.7 : 1
                  }}
                />
                {hasCertificate && (
                  <small style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                    Campo bloqueado - El curso tiene certificación configurada
                  </small>
                )}
              </div>

              <div className="cc-course-modal-grid">

                <div className="cc-course-modal-field">
                  <label>Tipo de Habilidad</label>
                  <select
                    value={configDraft.idTipoHabilidad}
                    onChange={(e) =>
                      setConfigDraft((prev) => ({
                        ...prev,
                        idTipoHabilidad: e.target.value,
                      }))
                    }
                  >
                    <option value="">Selecciona una Habilidad</option>
                    {tiposHabilidad.map((th) => (
                      <option key={th.idTipoHabilidad} value={th.idTipoHabilidad}>
                        {th.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="cc-course-modal-field">
                  <label>Nivel</label>
                  <select
                    value={configDraft.idNivel}
                    onChange={(e) =>
                      setConfigDraft((prev) => ({
                        ...prev,
                        idNivel: e.target.value,
                      }))
                    }
                  >
                    <option value="">Selecciona un nivel</option>
                    {niveles.map((nivel) => (
                      <option key={nivel.idNivel} value={nivel.idNivel}>
                        {nivel.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="cc-course-modal-field">
                <label>Monedas por Lección</label>
                <input
                  type="number"
                  min={course.terminado ? configMonedasBaseline : 0}
                  max="100"
                  value={configDraft.monedasPorLeccion}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== "" && Number(val) > 100) return;
                    setConfigDraft((prev) => ({
                      ...prev,
                      monedasPorLeccion: val,
                    }));
                  }}
                  placeholder="Ej. 10"
                  style={{
                    borderColor: course.terminado ? '#10b981' : undefined,
                    borderWidth: course.terminado ? '2px' : undefined
                  }}
                />
                <small className="cc-course-modal-hint" style={{
                  color: course.terminado ? '#10b981' : undefined,
                  fontWeight: course.terminado ? 600 : 'normal',
                  textAlign: 'center'
                }}>
                  {course.terminado
                    ? `✓ Curso publicado - Solo puedes aumentar las monedas (mínimo: ${configMonedasBaseline})`
                    : 'Esta cantidad se aplica a TODAS las lecciones del curso. Puedes modificarla libremente.'}
                </small>
              </div>

              <div className="cc-course-modal-grid">
                <div className="cc-course-modal-field" style={{ display: 'flex', alignItems: 'center', marginTop: '0.5rem', marginBottom: '1rem' }}>
                  <label style={{ marginBottom: 0 }}>Duración estimada del curso: {configDraft.duracion || 0} Días</label>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                padding: '12px 14px', 
                backgroundColor: '#f8fafc', 
                borderRadius: '10px', 
                border: '1px solid #e2e8f0',
                marginBottom: '1.25rem'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <label 
                    htmlFor="autoAsignableConfig" 
                    style={{ 
                      marginBottom: 0, 
                      fontWeight: 700, 
                      cursor: 'pointer', 
                      fontSize: '0.9rem', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      color: '#1e293b'
                    }}
                  >
                    Libre inscripción
                    <FiHelpCircle
                      style={{ color: '#64748b', fontSize: '0.9rem' }}
                      title="Si está encendido, el curso aparecerá en el catálogo para que cualquier estudiante se inscriba. Si está apagado, solo el personal de RH podrá asignarlo."
                    />
                  </label>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Permite la auto-inscripción de alumnos desde el catálogo.</span>
                </div>
                <label className="lms-toggle-switch">
                  <input
                    type="checkbox"
                    id="autoAsignableConfig"
                    checked={configDraft.autoAsignable}
                    onChange={(e) => setConfigDraft(prev => ({ ...prev, autoAsignable: e.target.checked }))}
                  />
                  <span className="lms-toggle-slider"></span>
                </label>
              </div>

              <div className="cc-course-modal-field">
                <label>Imagen del curso</label>
                {/* Área de carga con preview integrado */}
                <div
                  style={{
                    border: configDraft.urlRutaImagen
                      ? '2px solid #10b981'
                      : `2px dashed ${configImageError ? '#ef4444' : '#cbd5e1'}`,
                    borderRadius: 8,
                    padding: '0.75rem',
                    backgroundColor: configDraft.urlRutaImagen ? '#ecfdf5' : '#f9fafb',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                  }}
                >
                  {/* Badge de estado */}
                  {configDraft.urlRutaImagen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        zIndex: 10,
                      }}
                    >
                      <span>✓</span>
                      <span>Archivo cargado</span>
                    </div>
                  )}

                  {/* Vista previa si existe */}
                  {configDraft.urlRutaImagen && (
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{
                        fontSize: '0.85rem',
                        color: '#64748b',
                        marginBottom: '0.8rem',
                        fontWeight: 600
                      }}>
                        Vista previa:
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <img
                          src={configImagePreview || (
                            configDraft.urlRutaImagen?.startsWith('http://') || configDraft.urlRutaImagen?.startsWith('https://')
                              ? configDraft.urlRutaImagen
                              : `${SERVER_URL}${configDraft.urlRutaImagen}`
                          )}
                          alt="Vista previa del curso"
                          style={{
                            width: '100%',
                            maxHeight: 180,
                            borderRadius: 6,
                            objectFit: 'cover',
                            display: 'block',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Botón de carga */}
                  <label
                    htmlFor="cc-config-image-input"
                    style={{
                      cursor: course.terminado ? 'not-allowed' : (configImageUploading ? 'wait' : 'pointer'),
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: configDraft.urlRutaImagen ? '0.5rem' : '0',
                      opacity: (configImageUploading || course.terminado) ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleConfigImageChange}
                      style={{ display: 'none' }}
                      id="cc-config-image-input"
                      disabled={configImageUploading || course.terminado}
                    />
                    <div style={{ fontSize: '1.3rem', color: configDraft.urlRutaImagen ? '#059669' : '#64748b' }}>
                      {course.terminado ? '🔒' : (configImageUploading ? '⏳' : configDraft.urlRutaImagen ? '🔄' : '📷')}
                    </div>
                    <div style={{ color: configDraft.urlRutaImagen ? '#059669' : '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                      {course.terminado
                        ? 'Imagen bloqueada - Curso publicado'
                        : (configImageUploading ? 'Subiendo imagen...' : configDraft.urlRutaImagen ? 'Cambiar imagen' : 'Haz clic para subir imagen')}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: configDraft.urlRutaImagen ? '#059669' : '#94a3b8' }}>JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)</div>
                  </label>
                </div>

                {configImageError && (
                  <small style={{ color: '#b91c1c', fontSize: '.8rem', display: 'block', marginTop: 4 }}>
                    {configImageError}
                  </small>
                )}
              </div>
            </div>

            <div className="cc-course-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setConfigModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="cc-btn-primary"
                onClick={handleSaveConfig}
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Solicitar Certificado */}
      <SolicitarCertificadoModal
        open={solicitarCertModalOpen}
        onClose={() => setSolicitarCertModalOpen(false)}
        courseId={course?.idCurso}
        courseName={course?.titulo || course?.nombre || 'Curso'}
      />

      {/* Modales de Alerta y Confirmación */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        title={alertDialog.title}
        message={alertDialog.message}
        variant={alertDialog.variant}
      />

      {/* FAB para Observaciones */}
      <button
        className="fab-review"
        onClick={() => setIsObservacionesModalOpen(true)}
        title="Ver observaciones"
      >
        <FiMessageSquare />
      </button>

      {/* Modal de Observaciones */}
      <ObservacionesModal
        isOpen={isObservacionesModalOpen}
        onClose={() => setIsObservacionesModalOpen(false)}
        idCurso={course?.idCurso}
        currentContentId={null} // En vista general no filtramos por contenido
        currentContentType={null}
        onObservationAdded={() => { /* Opcional: recargar algo si fuera necesario */ }}
        readOnly={true}
        onReferenceClick={handleObservationReferenceClick}
      />

      {/* Dialogos de confirmación de eliminación */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
      />
    </div>
  );
};

export default CourseCurriculum;